import React, { useState, useCallback, useMemo, useContext, createContext, useRef, useEffect } from 'react';
import { useQuery, gql, useMutation, useLazyQuery } from '@apollo/client';
import _$B, { toInteger } from 'lodash';
import * as Sentry from '@sentry/react';
import { parseString } from 'xml2js';
import axios from 'axios';
export { default as axios } from 'axios';
import styled, { ThemeProvider as ThemeProvider$1, createGlobalStyle } from 'styled-components';
import InfiniteScroll from 'react-infinite-scroller';
import { Loader as Loader$5, Button as Button$5, Input as Input$2, Dimmer, Modal as Modal$1, Checkbox as Checkbox$2, Segment as Segment$3, Image, Dropdown as Dropdown$1, Card, Icon as Icon$2, Header as Header$2, Form, Popup, Radio } from 'semantic-ui-react';
import { Scrollbars } from 'react-custom-scrollbars-2';
import createMentionPlugin, { defaultTheme, defaultSuggestionsFilter } from '@draft-js-plugins/mention';
import { CompositeDecorator, EditorState, ContentState, convertToRaw, convertFromRaw, getDefaultKeyBinding } from 'draft-js';
import Editor from '@draft-js-plugins/editor';
import 'draft-js/dist/Draft.css';
import '@draft-js-plugins/mention/lib/plugin.css';
import PropTypes from 'prop-types';
import 'semantic-ui-css/semantic.min.css';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';

function _extends() {
  return _extends = Object.assign ? Object.assign.bind() : function (n) {
    for (var e = 1; e < arguments.length; e++) {
      var t = arguments[e];
      for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]);
    }
    return n;
  }, _extends.apply(null, arguments);
}

const defaultOptions = {
  dataField: undefined
};
/*
  How to use this hook
    1 - Define query type policies in src/core/services/graphql.js as described
      here: https://www.apollographql.com/docs/react/pagination/offset-based/#the-offsetlimitpagination-helper
      1.1 - Note that for each parameters combination there must have an entry (see testSessionNotes)
    2- Import hook and define 'dataField' option to the collection name
    3 - API pagination parameters must contain 'limit' and 'offset'
    4 - 'fetchPolicy' can be set to 'no-cache' to avoid storing data in the Apollo cache.
      We'll still hold merges in memory for the *current* query instance using 'updateQuery'.
*/
const usePaginatedQuery = (query, attributes, options = defaultOptions) => {
  const {
    dataField
  } = options;
  const {
    variables = {},
    fetchPolicy = "cache-first"
  } = attributes;
  const [hasMore, setHasMore] = useState(true);
  const {
    data = {},
    error,
    loading,
    fetchMore,
    refetch,
    startPolling,
    stopPolling
  } = useQuery(query, _extends({}, attributes, {
    fetchPolicy,
    variables: _extends({}, variables, {
      limit: variables.limit,
      offset: 0
    }),
    onCompleted: result => {
      const dataList = dataField && result[dataField];
      setHasMore(dataList && dataList.length ? dataList.length >= variables.limit : false);
    }
  }));
  /*
    handleLoadMore is called to get the next “page” of data. We'll do:
      fetchMore({ variables: { offset: currentData.length } })
    and define an updateQuery function that merges the old + new results
    for the "dataField" array.
  */
  const handleLoadMore = useCallback(async () => {
    if (!hasMore) return;
    if (!dataField) {
      return;
    }
    const currentData = Array.isArray(data[dataField]) ? data[dataField] : [];
    const result = await fetchMore({
      variables: {
        limit: variables.updateLimit || variables.limit,
        offset: currentData.length
      },
      updateQuery: (prev, {
        fetchMoreResult
      }) => {
        if (!prev) {
          // If there's no old data, the new data is basically the entire result
          return fetchMoreResult || {};
        }
        // If there's no new data, keep the old data
        if (!fetchMoreResult) {
          return prev;
        }
        // Otherwise, proceed with merging logic
        if (!dataField || !fetchMoreResult[dataField]) {
          return prev;
        }
        return _extends({}, prev, {
          [dataField]: [...(prev[dataField] || []), ...fetchMoreResult[dataField]]
        });
      }
    });
    let newData = [];
    if (dataField && result != null && result.data && Array.isArray(result.data[dataField])) {
      newData = result.data[dataField];
    }
    setHasMore(newData.length >= variables.limit);
  }, [hasMore, data, dataField, fetchMore, variables.limit]);
  return {
    data,
    error,
    hasMore,
    loading,
    fetchMore: handleLoadMore,
    refetch,
    startPolling,
    stopPolling
  };
};

let _$A = t => t,
  _t$A;
const GET_COMPANY_MEMBERS = gql(_t$A || (_t$A = _$A`
  query CompanyMembers($companyId: ID!) {
    companyMembers(companyId: $companyId, hidden: false) {
      id
      projectApproval
      user {
        id
        email
        profile {
          id
          firstName
          lastName
        }
      }
    }
  }
`));

const captureException = (event, error) => {
  Sentry.withScope(scope => {
    scope.setTag("event", event);
    scope.setLevel("error");
    Sentry.captureMessage(error);
  });
};
const captureGQLException = (event, message, path) => {
  Sentry.withScope(scope => {
    if (path) scope.setTag("path", path);
    scope.setTag("event", event);
    scope.setLevel("error");
    Sentry.captureMessage(message);
  });
};
const sendInfo = (event, message = event) => {
  Sentry.withScope(scope => {
    scope.setTag("event", event);
    scope.setLevel("info");
    Sentry.captureMessage(message);
  });
};

const useCompanyMembers = companyId => {
  const {
    data: companyMembersData = {},
    error,
    loading
  } = useQuery(GET_COMPANY_MEMBERS, {
    variables: {
      companyId
    }
  });
  if (error) {
    captureException("USE_COMPANY_MEMBERS_FAILED", error);
  }
  const {
    companyMembers
  } = companyMembersData;
  const mappedMembers = useMemo(() => _$B.map(companyMembers, member => ({
    id: member.user.id,
    name: `${member.user.profile.firstName} ${member.user.profile.lastName}`,
    companyMember: member,
    projectApproval: member.projectApproval
  })), [companyMembers]);
  return {
    data: mappedMembers,
    error,
    loading
  };
};

const MediaContext = React.createContext("media");
MediaContext.displayName = "MediaContext";
function Media({
  children
}) {
  const [playerState, setPlayerState] = useState({});
  const [playerSetters, setPlayerSetters] = useState({});
  const [player, setPlayer] = useState(null);
  const context = useMemo(() => {
    return {
      setPlayerState,
      setPlayerSetters,
      player,
      setPlayer,
      media: _extends({}, playerSetters, playerState)
    };
  }, [playerState, playerSetters]);
  return React.createElement(MediaContext.Provider, {
    value: context
  }, children);
}

function getCurrentTime(currentTime) {
  if (typeof currentTime === "function") return currentTime();
  return currentTime;
}
function useMedia() {
  const {
    media
  } = useContext(MediaContext);
  const newMedia = _extends({}, media, {
    currentTime: getCurrentTime(media.currentTime)
  });
  return newMedia;
}

const IfScopeContext = createContext({
  container: children => children
});
function IfScopeProvider({
  children,
  container
}) {
  const context = useMemo(() => {
    return {
      container
    };
  }, []);
  return React.createElement(IfScopeContext.Provider, {
    value: context
  }, children);
}

function useIfScope() {
  const context = useContext(IfScopeContext);
  const {
    container
  } = context;
  return {
    container
  };
}

const WebScopeContext = createContext({});
function WebScopeProvider({
  children,
  scope
}) {
  const context = useMemo(() => {
    return {
      scope
    };
  }, []);
  return React.createElement(WebScopeContext.Provider, {
    value: context
  }, children);
}

function useWebScope() {
  const context = useContext(WebScopeContext);
  const {
    scope
  } = context;
  return {
    scope
  };
}

let _$z = t => t,
  _t$z;
const CREATE_BILLING_PORTAL = gql(_t$z || (_t$z = _$z`
  mutation CreateBillingPortal($customerId: ID!) {
    createBillingPortal(customerId: $customerId) {
      portal {
        id
        url
      }
    }
  }
`));

const useCreateBillingPortalMutation = () => {
  const [createBillingPortal, {
    loading,
    error
  }] = useMutation(CREATE_BILLING_PORTAL);
  if (error) {
    captureException("CREATE_BILLING_PORTAL_FAILED", error);
  }
  const handleCreatePortal = async customerId => {
    if (!customerId) return null;
    const {
      data
    } = await createBillingPortal({
      variables: {
        customerId
      }
    });
    const {
      createBillingPortal: cachedCreateBillingPortal
    } = data;
    const {
      portal
    } = cachedCreateBillingPortal;
    return portal.url;
  };
  return {
    createPortal: customerId => handleCreatePortal(customerId),
    loading
  };
};

let _$y = t => t,
  _t$y;
const GET_FEATURE_FLAGS = gql(_t$y || (_t$y = _$y`
  query FeatureFlagsByCompany($companyId: ID!) {
    featureFlagsByCompany(companyId: $companyId) {
      featureName
      enabled
    }
  }
`));

const useFeatureFlags = companyId => {
  const [featureFlags, setFeatureFlags] = useState({});
  const camelCaseFeatureFlags = flags => {
    const transformedObject = flags.reduce((acc, {
      featureName,
      enabled
    }) => {
      const transformedKey = _$B.camelCase(featureName);
      acc[transformedKey] = enabled;
      return acc;
    }, {});
    return transformedObject;
  };
  const {
    loading,
    error
  } = useQuery(GET_FEATURE_FLAGS, {
    variables: {
      companyId
    },
    skip: !companyId,
    onCompleted: data => {
      setFeatureFlags(camelCaseFeatureFlags(data && data.featureFlagsByCompany));
    }
  });
  return {
    featureFlags,
    loading,
    error
  };
};

/**
 * Retrieves a presigned URL for uploading a file to S3.
 *
 * @param filename - The name of the file to be uploaded.
 * @param errorHandler - A callback function to handle errors.
 * @param contentType - The content type of the file (optional).
 * @returns The presigned URL for uploading the file, or null if an error occurred.
 */
const getPresignedUrl = async (axiosPlaybookux, errorHandler, filename, endpointUrl, contentType = null, extraData = null) => {
  if (!filename) return null;
  try {
    const options = _extends({
      filename,
      content_type: contentType
    }, typeof extraData === "object" && extraData !== null ? extraData : {});
    const {
      data
    } = await axiosPlaybookux.post(endpointUrl, {
      media: options
    }, {
      headers: {
        "Content-Type": "application/json"
      }
    });
    return data;
  } catch (e) {
    errorHandler(e);
    return null;
  }
};

/**
 * This class purpose is to cancel all requests
 * You can use this class to communicate with third party API like Google API, AWS API, etc.
 * Do not use this class to communicate with our Server API.
 * Don't update this code for our API Server.
 */
class AxiosThirdParty {
  constructor() {
    this.controllers = new Set();
    this.axiosInstance = void 0;
    this.dismounted = false;
    this.axiosInstance = axios.create();
    this.dismounted = false;
  }
  request(config) {
    // abort all requests oin going
    this.abortWhenDismounted();
    // create new controller
    const controller = new AbortController();
    this.controllers.add(controller);
    // abort next request
    if (this.dismounted) {
      controller.abort();
    }
    return this.axiosInstance.request(_extends({}, config, {
      signal: controller.signal
    })).finally(() => {
      this.controllers.delete(controller);
    });
  }
  abortAll() {
    this.controllers.forEach(controller => controller.abort());
    this.controllers.clear();
  }
  abortWhenDismounted() {
    if (this.dismounted) {
      this.abortAll();
    }
  }
  setDismounted(status) {
    this.dismounted = status;
  }
}
var axiosThirdParty = new AxiosThirdParty();

const MAX_FILE_SIZE = 1.5 * 1024 * 1024 * 1024;
const MAX_FILE_COUNT = 5;
const FILENAME_CHARACTERS = /^[a-zA-Z0-9._\-\s]+$/;
// Allowed file types for "research upload"
const ALLOWED_RESEARCH_FILE_TYPES = ["video/mp4", "video/quicktime", "video/webm"];
// Allowed file types for "attachments"
const ALLOWED_ATTACHMENT_FILE_TYPES = ["application/pdf", "video/mp4", "video/quicktime", "video/webm", "audio/mpeg"];
const checkInvalidUpload = (newFiles, files, setError, isAttachment, uploadResearchAvailableStorage) => {
  const isInvalidUpload = newFiles.some(file => {
    if (!FILENAME_CHARACTERS.test(file.name)) {
      setError("Please rename the file.\nOnly letters, numbers, dots, dashes, spaces and underscores are allowed.");
      return true;
    }
    if (file.name.length > 30) {
      setError("Please rename the file so it has a maximum of 30 characters");
      return true;
    }
    const allowedFileTypes = isAttachment ? ALLOWED_ATTACHMENT_FILE_TYPES : ALLOWED_RESEARCH_FILE_TYPES;
    if (!allowedFileTypes.includes(file.type)) {
      if (isAttachment) {
        setError("Allowed file types: pdf, mp4, mov, web, mp3");
      } else {
        setError("Allowed file types: mp4, mov, web");
      }
      return true;
    }
    if (file.size > MAX_FILE_SIZE) {
      setError(`Each ${isAttachment ? "files" : "videos"} should be 1.5 GB or smaller`);
      return true;
    }
    if (files.some(existingFile => existingFile.name === file.name)) {
      setError("You’ve already uploaded a file with the same name.\nPlease rename the file and upload again.");
      return true;
    }
    return false;
  });
  if (isInvalidUpload) return true;
  if (newFiles.length > MAX_FILE_COUNT) {
    setError(`You can only upload ${MAX_FILE_COUNT} ${isAttachment ? "files" : "videos"} at a time`);
    return true;
  }
  /* eslint-disable no-undef */
  const totalOldFilesBytes = files.reduce((acc, file) => BigInt(file.size) + acc, BigInt(0));
  const totalNewFilesBytes = newFiles.reduce((acc, file) => BigInt(file.size) + acc, BigInt(0));
  const totalFilesBytes = totalOldFilesBytes + totalNewFilesBytes;
  const availableStorageBytes = BigInt(uploadResearchAvailableStorage || 0);
  /* eslint-disable no-undef */
  if (availableStorageBytes - totalFilesBytes < 0n) {
    setError("You don´t have sufficient storage to upload the files.");
    return true;
  }
  return false;
};

const useErrorHandling = () => {
  const [errorUploadedFiles, setErrorUploadedFiles] = useState([]);
  const setErrorUploadedFilesIndex = useCallback((index, e) => {
    setErrorUploadedFiles(prevErrorUploadFiles => {
      const newError = [...prevErrorUploadFiles];
      newError[index] = e;
      return newError;
    });
  }, []);
  return {
    errorUploadedFiles,
    setErrorUploadedFilesIndex
  };
};
const usePresignedUrl = (values, setErrorUploadedFilesIndex, presignedUrl, axiosPlaybookux) => {
  const getPresignedUrlCallback = useCallback(async (filename, filesize, index, contentType = null) => getPresignedUrl(axiosPlaybookux, e => setErrorUploadedFilesIndex(index, e.message), filename, presignedUrl, contentType, _extends({}, values, {
    filesize
  })), [values, setErrorUploadedFilesIndex]);
  return {
    getPresignedUrlCallback
  };
};
const useRequestUpload = (projectId, onCompletedUpload, setErrorUploadedFilesIndex, setUploadProgress, errorMessageEntityTooLarge) => {
  const requestUpload = useCallback(async (index, url, filename, filesize, formData) => {
    try {
      const data = await axiosThirdParty.request({
        method: "post",
        url,
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data"
        },
        onUploadProgress: e => {
          const progress = Math.round(e.loaded * 100 / e.total);
          setUploadProgress(prevProgress => {
            const newProgress = [...prevProgress];
            newProgress[index] = progress;
            return newProgress;
          });
        }
      });
      if (data.status === 204 || data.status === 200) {
        onCompletedUpload({
          projectId,
          filename,
          filesize
        });
      }
    } catch (e) {
      const {
        response
      } = e;
      if (response) {
        parseString(response.data, (_err, result) => {
          if (result.Error.Code[0] === "EntityTooLarge") {
            setErrorUploadedFilesIndex(index, errorMessageEntityTooLarge);
          }
        });
      } else {
        setErrorUploadedFilesIndex(index, e.message);
      }
    }
  }, [projectId, onCompletedUpload, setErrorUploadedFilesIndex]);
  return {
    requestUpload
  };
};
const useFileValidation = (files, setFiles,
//others
isAttachment, uploadResearchAvailableStorage,
// state
setErrorUploadedFilesIndex, setError,
// hooks
getPresignedUrlCallback, requestUpload) => {
  const validateAndSetFiles = useCallback(async newFiles => {
    setError("");
    if (checkInvalidUpload(newFiles, files, setError, isAttachment, uploadResearchAvailableStorage) === true) return;
    newFiles.forEach(async (file, index) => {
      const contentType = file.type;
      // sum because we need to know the index of the file in the files array
      const newIndex = files.length + index;
      try {
        const data = await getPresignedUrlCallback(file.name, file.size, newIndex, contentType);
        if (!data) return;
        const {
          url,
          fields
        } = data;
        const formData = new FormData();
        Object.keys(fields).forEach(key => {
          formData.set(key, fields[key]);
        });
        formData.append("file", file, file.name);
        requestUpload(newIndex, url, data.fields.key, file.size, formData);
      } catch (e) {
        setErrorUploadedFilesIndex(newIndex, e.message);
      }
    });
    setFiles(prevFiles => _$B.uniqBy([...prevFiles, ...newFiles], "name"));
  }, [files, setFiles,
  //others
  isAttachment, uploadResearchAvailableStorage,
  // state
  setErrorUploadedFilesIndex, setError,
  // hooks
  getPresignedUrlCallback, requestUpload]);
  return {
    validateAndSetFiles
  };
};

let _$x = t => t,
  _t$x;
const Loader$4 = styled(Loader$5).attrs(props => ({
  active: true,
  inline: true,
  size: props.size || "medium"
}))(_t$x || (_t$x = _$x`
  &&.ui.loader {
    :after {
      border-color: ${0} transparent
        transparent;
    }
    :before {
      border: 0.2em solid rgba(0, 0, 0, 0.1);
    }
  }
`), props => props.theme.color.loader);

let _$w = t => t,
  _t$w,
  _t2$n,
  _t3$h;
const InfiniteComponent = styled(InfiniteScroll)(_t$w || (_t$w = _$w`
  display: inherit;
  flex-wrap: wrap;
  min-height: 100%;
`));
const ContainerLoader$1 = styled.div(_t2$n || (_t2$n = _$w`
  align-items: center;
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-top: 20px;
  padding-bottom: 20px;
`));
const Loader$3 = styled(Loader$4)(_t3$h || (_t3$h = _$w``));

function InfiniteScrollComponent(props) {
  const {
    children,
    hasMore,
    onLoadMore,
    getScrollParent,
    useWindow
  } = props;
  return React.createElement(InfiniteComponent, {
    pageStart: 0,
    loadMore: onLoadMore,
    hasMore: hasMore,
    loader: React.createElement(ContainerLoader$1, {
      key: 0
    }, React.createElement(Loader$3, null)),
    threshold: 60,
    initialLoad: false,
    useWindow: useWindow,
    getScrollParent: getScrollParent
  }, children);
}
InfiniteScrollComponent.defaultProps = {
  useWindow: false,
  getScrollParent: null
};

let _$v = t => t,
  _t$v,
  _t2$m;
const View = styled.div(_t$v || (_t$v = _$v`
  padding-right: 20px;
  padding-left: ${0};
`), ({
  borderless
}) => borderless ? "0" : "20px");
const StyledScrollBar = styled(Scrollbars)(_t2$m || (_t2$m = _$v``));

function ScrollBarContainer({
  borderless = false,
  children,
  onScroll,
  refScroll
}) {
  return React.createElement(StyledScrollBar, {
    ref: refScroll,
    autoHide: true,
    onScroll: onScroll,
    renderView: props => React.createElement(View, _extends({
      borderless: borderless
    }, props))
  }, children);
}

let _$u = t => t,
  _t$u,
  _t2$l;
const ContainerLoader = styled.div(_t$u || (_t$u = _$u`
  align-items: center;
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-top: 20px;
  padding-bottom: 20px;
`));
const Loader$2 = styled(Loader$4)(_t2$l || (_t2$l = _$u``));

function CenteredLoader() {
  return React.createElement(ContainerLoader, null, React.createElement(Loader$2, null));
}

function withMedia(WrappedComponent) {
  function Component(props) {
    return React.createElement(Media, null, React.createElement(MediaContext.Consumer, null, context => React.createElement(WrappedComponent, _extends({}, props, {
      media: context.media
    }))));
  }
  return Component;
}

const EntityTypes$1 = {
  MENTION: "mention"
};
const extractMentionHighlights$1 = richText => {
  if (!richText) {
    return [];
  }
  const {
    blocks,
    entityMap
  } = richText;
  return _$B.chain(blocks)
  // Add offset to each block
  .reduce((acc, block) => {
    let offset = 0;
    let length = 0;
    const lastBlock = _$B.last(acc);
    if (lastBlock) {
      offset = lastBlock.offset;
      length = lastBlock.text.length + 1; // account for new line
    }
    const blockWithOffset = _extends({}, block, {
      offset: offset + length
    });
    return [...acc, blockWithOffset];
  }, [])
  // Offset each entity range based on block offset
  .map(block => _extends({}, block, {
    entityRanges: block.entityRanges.map(entityRange => _extends({}, entityRange, {
      offset: entityRange.offset + block.offset
    }))
  })).flatMap("entityRanges").filter(entityRange => {
    const entity = entityMap[entityRange.key];
    return entity.type === EntityTypes$1.MENTION;
  }).map(entityRange => ({
    id: entityRange.key,
    charStart: entityRange.offset,
    charEnd: entityRange.offset + entityRange.length - 1,
    mention: true
  })).value();
};

let _$t = t => t,
  _t$t,
  _t2$k;
const Container$8 = styled.div(_t$t || (_t$t = _$t`
  display: flex;
  flex-direction: column;
  ${0}
  border-radius: 2px;
  box-sizing: border-box;
  margin: 0;
  color: rgba(0, 0, 0, 0.87);
  box-shadow: 0 0 0 0 transparent inset;
  font-family: Roboto;
  outline: 0;
  transition: color 0.1s ease, border-color 0.1s ease;
  line-height: 1.2857;
  ${0}
  ${0}
  & .mention {
    color: #477ae9;
    background-color: #dbf2fc;
    border-radius: 4px;
  }
  & .public-DraftEditorPlaceholder-inner {
    color: rgb(199, 199, 205);
  }
`), props => !props.borderless && `
    background-color: white;
    border: 1px solid #ECECEC;
    padding: .78571429em 1em;
  `, props => !props.autoResize && `
    & .DraftEditor-editorContainer {
      resize: vertical;
      min-height: 75px;
      overflow: auto;
    }
  `, props => props.scrollable && `
    & .DraftEditor-editorContainer {
      height: 75px;
      overflow-y: auto;
    }
  `);
styled.span(_t2$k || (_t2$k = _$t`
  color: #477ae9;
  background-color: #dbf2fc;
  border-radius: 4px;
`));

const mentionsPluginConfig = {
  theme: _extends({}, defaultTheme, {
    mention: EntityTypes$1.MENTION
  }),
  entityMutability: "IMMUTABLE"
};
const {
  decorators
} = createMentionPlugin(mentionsPluginConfig);
// @ts-ignore
const allDecorators = new CompositeDecorator([decorators[0]]);
const createEmptyState = () => EditorState.createEmpty();
const createFromText = text => {
  const contentState = ContentState.createFromText(text);
  let editorState = EditorState.createEmpty();
  editorState = EditorState.push(editorState, contentState, "remove-range");
  return editorState;
};
const pushStateChange = (state, text) => {
  const contentState = ContentState.createFromText(text);
  let editorState = EditorState.push(state, contentState, "remove-range");
  editorState = EditorState.forceSelection(editorState, contentState.getSelectionAfter());
  return editorState;
};
const convertToPlainText = editorState => editorState.getCurrentContent().getPlainText();
const convertToRichText = editorState => convertToRaw(editorState.getCurrentContent());
const convertFromRichText = richText => {
  const content = convertFromRaw(richText);
  return EditorState.createWithContent(content, allDecorators);
};
const createEditorStateFromNote = testSessionNote => {
  const {
    note,
    noteRichText
  } = testSessionNote;
  if (noteRichText) {
    return convertFromRichText(JSON.parse(noteRichText));
  }
  return createFromText(note);
};
var EDITOR_STATUS;
(function (EDITOR_STATUS) {
  EDITOR_STATUS["HANDLED"] = "handled";
  EDITOR_STATUS["NOT_HANDLED"] = "not-handled";
  EDITOR_STATUS["SUBMIT"] = "submit";
})(EDITOR_STATUS || (EDITOR_STATUS = {}));
function RichTextEditor(props) {
  const {
    autoResize = false,
    borderless = false,
    editorState,
    onChange,
    placeholder = "",
    readOnly = false,
    userSuggestions,
    onKeyDown,
    submitOnEnter = false,
    scrollable = false,
    onCommentMention
  } = props;
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [mentions, setMentions] = useState([]);
  const handleMentionedPerson = mention => {
    setMentions(prevMentions => [...prevMentions, mention.companyMember.user.id]);
  };
  const editor = useRef(null);
  const focus = () => {
    var _editor$current;
    editor == null || (_editor$current = editor.current) == null || _editor$current.focus();
  };
  const handleKeyBinding = evt => {
    if (evt.keyCode === 13 && evt.shiftKey === false && submitOnEnter) {
      return EDITOR_STATUS.SUBMIT;
    }
    return getDefaultKeyBinding(evt);
  };
  const handleKeyCommand = command => {
    if (onKeyDown) {
      const handled = onKeyDown(command);
      if (mentions.length > 0 && onCommentMention) {
        onCommentMention(mentions, editorState);
        setMentions([]);
      }
      return handled;
    }
    return EDITOR_STATUS.NOT_HANDLED;
  };
  const mentionPlugin = useMemo(() => createMentionPlugin(mentionsPluginConfig), []);
  const {
    MentionSuggestions
  } = mentionPlugin;
  const plugins = [mentionPlugin];
  const handleSearchChange = ({
    value
  }) => {
    if (userSuggestions) {
      setSuggestions(defaultSuggestionsFilter(value, userSuggestions));
    }
  };
  const onOpenChange = useCallback(_open => {
    setOpen(_open);
  }, []);
  useEffect(() => {
    if (userSuggestions) {
      setSuggestions(userSuggestions);
    }
  }, [userSuggestions]);
  return React.createElement(Container$8, {
    autoResize: autoResize,
    borderless: borderless,
    onClick: focus,
    scrollable: scrollable
  }, React.createElement(Editor, {
    readOnly: readOnly,
    onChange: onChange,
    placeholder: placeholder,
    ref: editor,
    editorState: editorState,
    plugins: plugins,
    handleKeyCommand: handleKeyCommand,
    keyBindingFn: handleKeyBinding
  }), React.createElement(MentionSuggestions, {
    onSearchChange: handleSearchChange,
    suggestions: suggestions,
    open: open,
    onOpenChange: onOpenChange,
    onAddMention: handleMentionedPerson
  }));
}

let _$s = t => t,
  _t$s,
  _t2$j,
  _t3$g,
  _t4$d,
  _t5$b,
  _t6$8,
  _t7$7,
  _t8$6,
  _t9$4,
  _t10$3,
  _t11$3,
  _t12$3;
const Text$2 = styled.p.attrs(props => ({
  color: props.color || props.theme.color.text,
  fontSize: props.fontSize || props.theme.fontSize.text,
  unselectable: props.unselectable
}))(_t$s || (_t$s = _$s`
  color: ${0};
  margin-bottom: 0px;
  white-space: pre-wrap;

  -moz-user-select: ${0};
  -khtml-user-select: ${0};
  -webkit-user-select: ${0};
  -ms-user-select: ${0};
  user-select: ${0};
`), props => props.color, props => props.unselectable ? "-moz-none" : "text", props => props.unselectable ? "none" : "text", props => props.unselectable ? "none" : "text", props => props.unselectable ? "none" : "text", props => props.unselectable ? "none" : "text");
// font-size: ${(props) => props.fontSize};
Text$2.Header = styled(Text$2).attrs(props => _extends({}, props, {
  unselectable: props.unselectable
}))(_t2$j || (_t2$j = _$s`
  color: ${0};
  font-size: ${0};
  font-weight: 500;
`), props => props.theme.color.textHeader, props => props.theme.fontSize.textHeader);
Text$2.Title = styled(Text$2)(_t3$g || (_t3$g = _$s`
  color: ${0};
  font-size: ${0};
`), props => props.theme.color.textTitle, props => props.theme.fontSize.textTitle);
Text$2.Muted = styled(Text$2)(_t4$d || (_t4$d = _$s`
  color: ${0};
  font-size: ${0};
`), props => props.theme.color.textMuted, props => props.theme.fontSize.textMuted);
Text$2.Bold = styled(Text$2).attrs(props => _extends({}, props, {
  unselectable: props.unselectable
}))(_t5$b || (_t5$b = _$s`
  font-weight: 500;
`));
Text$2.Error = styled(Text$2)(_t6$8 || (_t6$8 = _$s`
  color: ${0};
`), props => props.theme.color.textError);
Text$2.Span = styled.span.attrs(props => ({
  color: props.color || props.theme.color.text,
  fontSize: props.fontSize || props.theme.fontSize.text
}))(_t7$7 || (_t7$7 = _$s`
  color: ${0};
  margin-bottom: 0px;
`), props => props.color);
// font-size: ${(props) => props.fontSize};
Text$2.Label = styled(Text$2)(_t8$6 || (_t8$6 = _$s`
  color: ${0};
`), props => props.theme.color.textLabel);
Text$2.Optional = styled(Text$2)(_t9$4 || (_t9$4 = _$s`
  color: ${0};
  font-size: ${0};
`), props => props.theme.color.textOptional, props => props.theme.fontSize.textOptional);
Text$2.Feedback = styled(Text$2)(_t10$3 || (_t10$3 = _$s`
  color: ${0};
`), props => props.theme.color.textFeedback);
Text$2.Secondary = styled(Text$2)(_t11$3 || (_t11$3 = _$s`
  color: ${0};
`), ({
  theme
}) => theme.color.textSecondary);
Text$2.Success = styled(Text$2)(_t12$3 || (_t12$3 = _$s`
  color: ${0};
`), props => props.theme.color.success);

let _$r = t => t,
  _t$r,
  _t2$i,
  _t3$f,
  _t4$c,
  _t5$a;
const Button$4 = styled(Button$5).attrs(props => ({
  fontSize: props.fontSize || props.theme.fontSize.button
}))(_t$r || (_t$r = _$r`
  &.ui.button {
    background: ${0};
    border-radius: 3px;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.25);
    color: ${0};
    font-size: ${0};
    font-weight: 500;
    margin: 0px;
    padding-left: 15px;
    padding-right: 15px;

    :hover {
      background: ${0};
    }

    :active {
      background: ${0};
    }
  }
`), props => props.theme.color.button, props => props.theme.color.buttonText, props => props.fontSize, props => props.theme.color.buttonHover, props => props.theme.color.buttonActive);
Button$4.Rounded = styled(Button$4)(_t2$i || (_t2$i = _$r`
  &.ui.button {
    border-radius: 50px;
    height: 45px;
    padding-left: 0px;
    padding-right: 0px;
    width: 45px;
  }
`));
Button$4.Label = styled(Button$4)(_t3$f || (_t3$f = _$r`
  &.ui.button {
    font-size: 14px;
    pointer-events: none;
  }
`));
Button$4.Inverted = styled(Button$4)(_t4$c || (_t4$c = _$r`
  &.ui.button {
    background: ${0};
    color: ${0};

    ${0}

    :hover {
      background: ${0};
    }

    :active {
      background: ${0};
    }
  }
`), props => props.theme.color.buttonInverted, props => props.theme.color.buttonInvertedText, ({
  theme,
  border
}) => {
  if (!border) return "";
  return `
                box-shadow: none;
                border: 1px solid ${theme.color.text};
                transition: background-color .1s ease;
              `;
}, props => props.theme.color.buttonInvertedHover, props => props.theme.color.buttonInvertedActive);
Button$4.Danger = styled(Button$4)(_t5$a || (_t5$a = _$r`
  &.ui.button {
    background: ${0};
    color: ${0};

    :hover {
      background: ${0};
    }

    :active {
      background: ${0};
    }
  }
  width: 160px;
`), props => props.theme.color.warningHeader, props => props.theme.color.white, props => props.theme.color.invertedDanger, props => props.theme.color.invertedDanger);

let _$q = t => t,
  _t$q,
  _t2$h,
  _t3$e;
const Input$1 = styled(Input$2)(_t$q || (_t$q = _$q`
  &&& {
    width: 100%;
    margin-top: 25px;
    ${0}
  }
`), props => {
  if (!props.under || !props.urlCopied && props.under) {
    return "margin-bottom: 25px;";
  }
  return `
      text-align: right;
      margin-bottom: 0;`;
});
const CopiedMessage = styled(Text$2)(_t2$h || (_t2$h = _$q`
  font-size: 14px;
  ${0}
`), props => props.under && `
    text-align: right;
    margin-bottom: 11px;
  `);
const Button$3 = styled(Button$4)(_t3$e || (_t3$e = _$q`
  &.ui.button {
    margin-bottom: 40px;
    margin-top: 20px;
    width: 75px;
  }
`));

function CopyInput({
  uri,
  text,
  copied,
  under = false
}) {
  const [urlCopied, setUrlCopied] = useState(false);
  const copyLinkRef = useRef();
  const onUrlCopy = () => {
    copyLinkRef.current.select();
    // TODO: change this deprecated command
    document.execCommand("copy");
    setUrlCopied(true);
  };
  return React.createElement(React.Fragment, null, React.createElement(Input$1, {
    ref: copyLinkRef,
    label: React.createElement(Button$3, {
      onClick: onUrlCopy,
      "aria-label": "Copy code"
    }, text),
    labelPosition: "right",
    value: uri,
    urlCopied: urlCopied,
    under: under
  }), urlCopied ? React.createElement(CopiedMessage, {
    under: under
  }, copied) : null);
}

let _$p = t => t,
  _t$p,
  _t2$g,
  _t3$d,
  _t4$b,
  _t5$9,
  _t6$7,
  _t7$6,
  _t8$5,
  _t9$3,
  _t10$2,
  _t11$2,
  _t12$2,
  _t13$1;
styled(Dimmer)(_t$p || (_t$p = _$p``));
const Dialog$1 = styled(Modal$1)(_t2$g || (_t2$g = _$p`
  &&& {
    padding: 5px 20px 30px;
    width: 500px;
  }
`));
const Loader$1 = styled(CenteredLoader)(_t3$d || (_t3$d = _$p``));
const Content$2 = styled(Modal$1.Content)(_t4$b || (_t4$b = _$p``));
const Title$1 = styled(Text$2.Title)(_t5$9 || (_t5$9 = _$p`
  text-align: left;
  margin-bottom: 10px;
`));
const ContainerToggle$1 = styled.div(_t6$7 || (_t6$7 = _$p`
  flex-direction: row;
  display: flex;
`));
const Toggle$1 = styled(Checkbox$2)(_t7$6 || (_t7$6 = _$p``));
const Text$1 = styled(Text$2)(_t8$5 || (_t8$5 = _$p`
  margin-right: 10px;
`));
const URL$1 = styled(CopyInput)(_t9$3 || (_t9$3 = _$p``));
const Input = styled(Input$2)(_t10$2 || (_t10$2 = _$p`
  &&&&&&& {
    width: 100%;
    margin: 25px 12px 25px 0;
  }
`));
const Button$2 = styled(Button$4)(_t11$2 || (_t11$2 = _$p``));
const ContainerButton = styled.div(_t12$2 || (_t12$2 = _$p`
  display: flex;
  align-items: center;
`));
const ErrorMessage$1 = styled(Text$2)(_t13$1 || (_t13$1 = _$p`
  color: ${0};
`), props => props.theme.color.error);

const withoutDialect = language => {
  if (_$B.includes(language, "-")) {
    // TODO: change this deprecated command
    const dialect = language.substr(language.indexOf("-") + 0);
    return _$B.replace(language, dialect, "");
  }
  return language;
};
function handlePhrases(locale, translations) {
  let dialect;
  dialect = _$B.find(translations, element => element[0] === locale);
  if (!dialect) {
    const language = withoutDialect(locale);
    const dialects = [];
    _$B.filter(translations, translation => {
      const key = translation[0];
      if (withoutDialect(key) === language) {
        dialects.push(translation);
      }
    });
    dialect = _$B.find(dialects, element => element[0] === language) || _$B.first(dialects);
  }
  const direction = dialect && _$B.includes(dialect, "Right to left") ? "rtl" : "ltr";
  if (_$B.isEmpty(dialect)) {
    return {
      phrases: {},
      direction
    };
  }
  const phrases = _$B.chain(translations.nameKey).map((key, index) => [key, dialect[index]]).fromPairs().value();
  phrases.translate = (textKey, substitutions) => {
    let newValue = _$B.cloneDeep(phrases);
    _$B.forEach(Object.keys(substitutions), key => {
      newValue = _$B.replace(newValue[textKey], `%${key}`, substitutions[key]);
    });
    return newValue;
  };
  return {
    direction,
    phrases
  };
}

const TranslationContext = React.createContext({});
function TranslationProvider(props) {
  const {
    children,
    defaultLocale = "en",
    source
  } = props;
  const [phrases, setPhrases] = useState({});
  const [locale, setLocale] = useState();
  const [direction, setDirection] = useState("");
  const handleLocaleChange = newLocale => {
    const {
      phrases: newPhrases,
      direction: newDirection
    } = handlePhrases(newLocale, source) || handlePhrases("en", source);
    setPhrases(newPhrases);
    setLocale(newLocale);
    setDirection(newDirection);
  };
  const setNewLocale = () => {
    const currentLocation = window.location.href;
    const url = new URL(currentLocation);
    const newLocale = locale || url.searchParams.get("lang") || window.navigator.language || defaultLocale;
    handleLocaleChange(newLocale);
  };
  useEffect(() => {
    setNewLocale();
  }, []);
  const context = useMemo(() => {
    return {
      direction,
      phrases,
      locale,
      setLocale: handleLocaleChange
    };
  }, [direction, phrases, locale]);
  return React.createElement(TranslationContext.Provider, {
    value: context
  }, children);
}

function withTranslation(WrappedComponent) {
  function Component(props) {
    const {
      direction,
      phrases,
      locale,
      setLocale
    } = useContext(TranslationContext);
    return React.createElement(WrappedComponent, _extends({
      direction: direction,
      phrases: phrases,
      language: locale,
      setLocale: setLocale
    }, props));
  }
  return Component;
}

function ShareLinkDialog(props) {
  const [enableShare, setEnableShare] = useState(false);
  const [passwordProtected, setPasswordProtected] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [password, setPassword] = useState("");
  const {
    show = false,
    handleClose,
    phrases,
    uri,
    handleUpdate,
    title,
    sharePassword,
    shareEnabled = false,
    toggleLabel,
    loading = false
  } = props;
  useEffect(() => {
    if (shareEnabled) {
      setEnableShare(true);
      if (sharePassword && !_$B.isEmpty(sharePassword)) {
        setPassword(sharePassword);
        setPasswordProtected(true);
      } else {
        setPassword("");
        setPasswordProtected(false);
      }
    } else {
      setEnableShare(false);
      setPassword("");
      setPasswordProtected(false);
    }
  }, [show]);
  useEffect(() => setPasswordError(false), [passwordProtected]);
  const handleChangeToggle = () => {
    setEnableShare(!enableShare);
    setPassword("");
    handleUpdate("", !enableShare);
  };
  const handleClickSave = e => {
    if (passwordProtected && _$B.isEmpty(password)) {
      setPasswordError(true);
    } else {
      handleUpdate(password, enableShare);
      handleClose(e);
    }
  };
  const handleCloseModal = e => {
    if (_$B.isEmpty(sharePassword) && _$B.isEmpty(password)) {
      setPassword("");
      setPasswordProtected(false);
    }
    setPasswordError(false);
    handleClose(e);
  };
  const handlePasswordToggle = () => {
    setPassword("");
    setPasswordProtected(!passwordProtected);
    handleUpdate("", enableShare);
  };
  const handleChangePassword = (_event, field) => {
    setPasswordError(false);
    setPassword(field.value);
  };
  return React.createElement(Dialog$1, {
    open: show,
    closeOnEscape: true,
    closeOnDimmerClick: true,
    onClose: handleCloseModal
  }, React.createElement(Content$2, null, React.createElement(Title$1, null, title), React.createElement(ContainerToggle$1, null, React.createElement(Text$1, null, toggleLabel), React.createElement(Toggle$1, {
    toggle: true,
    disabled: loading,
    checked: enableShare,
    onChange: handleChangeToggle
  })), loading && enableShare && React.createElement(Loader$1, null), enableShare && !loading && React.createElement(React.Fragment, null, React.createElement(URL$1, {
    uri: uri,
    text: phrases.copy_button,
    copied: phrases.copied_to_clipboard,
    under: true
  }), React.createElement(ContainerToggle$1, null, React.createElement(Text$1, null, "Password Protected"), React.createElement(Toggle$1, {
    toggle: true,
    checked: passwordProtected,
    onChange: handlePasswordToggle
  })), passwordProtected && React.createElement(ContainerButton, null, React.createElement(Input, {
    onChange: handleChangePassword,
    type: "text",
    placeholder: "Enter password",
    value: password
  }), React.createElement(Button$2, {
    onClick: handleClickSave
  }, "Save")), passwordError && passwordProtected && React.createElement(ErrorMessage$1, null, "Please enter a password"))));
}
var ShareLinkDialog$1 = withTranslation(ShareLinkDialog);

let _$o = t => t,
  _t$o;
const UPDATE_NOTE_SHARE_LINK = gql(_t$o || (_t$o = _$o`
  mutation UpdateNoteShareLink(
    $id: ID!
    $sharePassword: String!
    $shareEnabled: Boolean!
  ) {
    updateNoteShareLink(
      id: $id
      sharePassword: $sharePassword
      shareEnabled: $shareEnabled
    ) {
      note {
        id
        shareEnabled
        shareCode
        sharePassword
      }
    }
  }
`));

function ShareClipDialog(props) {
  const {
    note,
    show = false,
    onClose,
    defaultUrl
  } = props;
  const {
    id,
    shareCode,
    sharePassword,
    shareEnabled
  } = note;
  const uri = `${defaultUrl}/share/clip/${shareCode}`;
  const [updateShareLinkMutation] = useMutation(UPDATE_NOTE_SHARE_LINK);
  const handleUpdate = (password, enableShare) => {
    updateShareLinkMutation({
      variables: {
        id,
        sharePassword: password,
        shareEnabled: enableShare
      }
    });
  };
  return React.createElement(ShareLinkDialog$1, {
    title: "Share Clip",
    uri: uri,
    show: show,
    sharePassword: sharePassword,
    shareEnabled: shareEnabled,
    toggleLabel: "Enable sharing",
    handleUpdate: handleUpdate,
    handleClose: onClose
  });
}

let _$n = t => t,
  _t$n,
  _t2$f,
  _t3$c,
  _t4$a,
  _t5$8,
  _t6$6,
  _t7$5,
  _t8$4,
  _t9$2,
  _t10$1,
  _t11$1,
  _t12$1;
const TAG_COUNT_FRAGMENT = gql(_t$n || (_t$n = _$n`
  fragment TagCount on Tag {
    count
  }
`));
const TAG_HIGHLIGHTS_WITH_TAG_FRAGMENT = gql(_t2$f || (_t2$f = _$n`
  fragment TagHighlightWithTag on TagHighlight {
    id
    charStart
    charEnd
    tag {
      id
      name
      tagType
      ...TagCount
      tagGroup {
        id
        color
      }
    }
  }
  ${0}
`), TAG_COUNT_FRAGMENT);
gql(_t3$c || (_t3$c = _$n`
  query Tags($companyId: ID!) {
    tags(companyId: $companyId) {
      id
      name
      count
      description
      tagType
      tagGroup {
        id
      }
    }
    tagGroups(companyId: $companyId) {
      id
      name
      description
      color
    }
  }
`));
gql(_t4$a || (_t4$a = _$n`
  mutation CreateTag(
    $name: String!
    $description: String
    $tagGroupId: ID
    $companyId: ID!
  ) {
    createTag(
      name: $name
      description: $description
      tagGroupId: $tagGroupId
      companyId: $companyId
    ) {
      tag {
        id
        name
        count
        tagType
        description
        tagGroup {
          id
          name
          description
          color
        }
        createdBy {
          id
        }
      }
    }
  }
`));
gql(_t5$8 || (_t5$8 = _$n`
  mutation CreateMergedTag(
    $name: String!
    $tagGroupId: ID
    $companyId: ID!
    $tagIds: [ID!]!
  ) {
    createMergedTag(
      name: $name
      tagGroupId: $tagGroupId
      companyId: $companyId
      tagIds: $tagIds
    ) {
      tag {
        id
        name
        count
        tagType
        description
        tagGroup {
          id
          name
          description
          color
        }
        createdBy {
          id
        }
      }
    }
  }
`));
gql(_t6$6 || (_t6$6 = _$n`
  mutation UpdateTag($id: ID!, $attributes: TagAttributes!) {
    updateTag(id: $id, attributes: $attributes) {
      tag {
        id
        name
        count
        tagType
        description
        tagGroup {
          id
          name
          description
          color
          company {
            id
            name
          }
        }
        createdBy {
          id
        }
      }
    }
  }
`));
gql(_t7$5 || (_t7$5 = _$n`
  mutation CreateTagGroup(
    $name: String!
    $description: String
    $color: String!
    $companyId: ID!
  ) {
    createTagGroup(
      name: $name
      description: $description
      color: $color
      companyId: $companyId
    ) {
      tagGroup {
        id
        name
        description
        color
      }
    }
  }
`));
gql(_t8$4 || (_t8$4 = _$n`
  mutation CreateMergedTagGroup(
    $name: String!
    $description: String
    $color: String!
    $companyId: ID!
    $tagGroupIds: [ID!]!
  ) {
    createMergedTagGroup(
      name: $name
      description: $description
      color: $color
      companyId: $companyId
      tagGroupIds: $tagGroupIds
    ) {
      tagGroup {
        id
        name
        description
        color
      }
    }
  }
`));
gql(_t9$2 || (_t9$2 = _$n`
  mutation UpdateTagGroup($id: ID!, $attributes: TagGroupAttributes!) {
    updateTagGroup(id: $id, attributes: $attributes) {
      tagGroup {
        id
        name
        description
        color
        company {
          id
        }
      }
    }
  }
`));
gql(_t10$1 || (_t10$1 = _$n`
  mutation DeleteTagGroup($id: ID!) {
    deleteTagGroup(id: $id) {
      void
    }
  }
`));
gql(_t11$1 || (_t11$1 = _$n`
  mutation DeleteTag($id: ID!) {
    deleteTag(id: $id) {
      void
    }
  }
`));
gql(_t12$1 || (_t12$1 = _$n`
  mutation CreateTagHighlight($tagHighlights: [TagHighlightAttributes!]!) {
    createTagHighlight(tagHighlights: $tagHighlights) {
      tagHighlights {
        ...TagHighlightWithTag
      }
    }
  }
  ${0}
`), TAG_HIGHLIGHTS_WITH_TAG_FRAGMENT);

let _$m = t => t,
  _t$m,
  _t2$e,
  _t3$b,
  _t4$9,
  _t5$7,
  _t6$5,
  _t7$4,
  _t8$3;
const NOTE_TAG_HIGHLIGHTS_FRAGMENT = gql(_t$m || (_t$m = _$m`
  fragment NoteTagHighlights on TestSessionNote {
    tagHighlights {
      ...TagHighlightWithTag
    }
  }
  ${0}
`), TAG_HIGHLIGHTS_WITH_TAG_FRAGMENT);
const TEST_SESSION_NOTE_FRAGMENT = gql(_t2$e || (_t2$e = _$m`
  fragment TestSessionNote on TestSessionNote {
    id
    videoStartTime
    videoEndTime
    videoUrl
    note
    noteType
    noteRichText
    createdAt
    shareEnabled
    shareCode
    sharePassword
    status
    createdBy {
      id
      profile {
        id
        firstName
      }
    }
    ...NoteTagHighlights
  }
  ${0}
`), NOTE_TAG_HIGHLIGHTS_FRAGMENT);
const CREATE_TEST_SESSION_NOTE = gql(_t3$b || (_t3$b = _$m`
  mutation CreateTestSessionNote(
    $testSessionId: ID!
    $attributes: TestSessionNotesAttributes!
  ) {
    createTestSessionNote(
      testSessionId: $testSessionId
      attributes: $attributes
    ) {
      testSessionNote {
        ...TestSessionNote
      }
    }
  }
  ${0}
`), TEST_SESSION_NOTE_FRAGMENT);
const TEST_SESSION_NOTES = gql(_t4$9 || (_t4$9 = _$m`
  query TestSessionNotes(
    $testSessionId: ID!
    $limit: Int!
    $offset: Int!
    $order: String!
  ) {
    testSessionNotes(
      testSessionId: $testSessionId
      limit: $limit
      offset: $offset
      order: $order
    ) {
      ...TestSessionNote
    }
  }
  ${0}
`), TEST_SESSION_NOTE_FRAGMENT);
const DELETE_TEST_SESSION_NOTE = gql(_t5$7 || (_t5$7 = _$m`
  mutation DeleteTestSessionNote($noteId: ID!) {
    deleteTestSessionNote(noteId: $noteId) {
      void
    }
  }
`));
const UPDATE_TEST_SESSION_NOTE = gql(_t6$5 || (_t6$5 = _$m`
  mutation UpdateTestSessionNote(
    $id: ID!
    $attributes: TestSessionNotesAttributes!
  ) {
    updateTestSessionNote(id: $id, attributes: $attributes) {
      testSessionNote {
        ...TestSessionNote
      }
    }
  }
  ${0}
`), TEST_SESSION_NOTE_FRAGMENT);
gql(_t7$4 || (_t7$4 = _$m`
  query TestSessionNote($id: ID!) {
    testSessionNote(id: $id) {
      id
      videoStartTime
      videoEndTime
      videoUrl
      status
      note
      noteType
      createdAt
      shareEnabled
      shareCode
      sharePassword
      createdBy {
        id
        profile {
          id
          firstName
        }
      }
    }
  }
`));
const DELETE_TAG_HIGHLIGHT = gql(_t8$3 || (_t8$3 = _$m`
  mutation DeleteTagHighlight($ids: [ID!]!) {
    deleteTagHighlight(ids: $ids) {
      void
    }
  }
`));
const LIMIT = 10;
const updateNoteTagHighlightsDeleted = (noteId, tagHighlight) => cache => {
  // Remove TagHighlight from TestSessionNote
  let {
    tagHighlights
  } = cache.readFragment({
    id: `TestSessionNote:${noteId}`,
    fragment: NOTE_TAG_HIGHLIGHTS_FRAGMENT,
    fragmentName: "NoteTagHighlights"
  });
  tagHighlights = tagHighlights.filter(({
    id
  }) => id !== tagHighlight.id);
  cache.writeFragment({
    id: `TestSessionNote:${noteId}`,
    fragment: NOTE_TAG_HIGHLIGHTS_FRAGMENT,
    fragmentName: "NoteTagHighlights",
    data: {
      tagHighlights,
      __typename: "TestSessionNote"
    }
  });
  // Update Tag count
  const {
    count
  } = cache.readFragment({
    id: `Tag:${tagHighlight.tag.id}`,
    fragment: TAG_COUNT_FRAGMENT
  });
  cache.writeFragment({
    id: `Tag:${tagHighlight.tag.id}`,
    fragment: TAG_COUNT_FRAGMENT,
    data: {
      count: count - 1,
      __typename: "Tag"
    }
  });
};

let _$l = t => t,
  _t$l;
const Segment$2 = styled(Segment$3).attrs(props => ({
  "data-id": props.dataId,
  "data-entity": props.dataEntity
}))(_t$l || (_t$l = _$l`
  &.ui.segment {
    border-radius: 2px;
    border: 1px solid ${0};
    box-shadow: none;
    margin: 0px;
  }
`), props => props.theme.color.segmentBorder);

let _$k = t => t,
  _t$k;
const Container$7 = styled.div.attrs(props => ({
  "data-id": props.dataId,
  "data-entity": props.dataEntity
}))(_t$k || (_t$k = _$k``));

function PolymorphicContainer({
  dataId,
  dataEntity,
  children
}) {
  return /*#__PURE__*/React.createElement(Container$7, {
    dataId: dataId,
    dataEntity: dataEntity
  }, children);
}
PolymorphicContainer.propTypes = {
  dataId: PropTypes.string.isRequired,
  dataEntity: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired
};

let _$j = t => t,
  _t$j,
  _t2$d,
  _t3$a,
  _t4$8,
  _t5$6,
  _t6$4,
  _t7$3,
  _t8$2,
  _t9$1,
  _t10,
  _t11,
  _t12,
  _t13,
  _t14,
  _t15,
  _t16,
  _t17,
  _t18,
  _t19;
const Segment$1 = styled(Segment$2)(_t$j || (_t$j = _$j`
  &.ui.segment {
    margin-top: 20px;

    :last-child {
      margin-bottom: 60px;
    }
  }
`));
const SegmentTextArea = styled(Segment$2)(_t2$d || (_t2$d = _$j``));
const ContainerNotes = styled.div(_t3$a || (_t3$a = _$j`
  flex: 1;
  overflow-y: hidden;
  height: 100%;
`));
const ContainerNote = styled(PolymorphicContainer)(_t4$8 || (_t4$8 = _$j``));
const NoteName = styled(Text$2)(_t5$6 || (_t5$6 = _$j`
  font-weight: 500;
  font-size: 14px;

  :last-child {
    margin-left: 5px;
    color: #477ae9;
    cursor: pointer;
  }
`));
const NoteTimeStampsSecondary = styled(Text$2.Secondary)(_t6$4 || (_t6$4 = _$j`
  margin-left: 5px;
`));
const NoteHeaderContainer = styled.div(_t7$3 || (_t7$3 = _$j`
  display: flex;
  flex-direction: row;
`));
styled.div(_t8$2 || (_t8$2 = _$j`
  align-items: center;
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-top: 35px;
`));
const ImageIcon = styled(Image)(_t9$1 || (_t9$1 = _$j`
  &.ui.image {
    width: 18px;
    height: 4px;
  }
`));
styled.div(_t10 || (_t10 = _$j`
  font: "Roboto";
  font-size: 23pt;
  color: #5e6c84;
  float: right;
`));
styled(Segment$2)(_t11 || (_t11 = _$j`
  &.ui.segment {
    position: absolute;
    right: 10px;
    top: 24px;
    padding: 8px 12px;
  }
`));
styled.div.attrs(props => {
  const color = props.color || "#ED3A4E";
  return {
    color
  };
})(_t12 || (_t12 = _$j`
  display: inline-block;
  font: "Roboto Regular";
  font-size: 12px;
  color: ${0};
  border: 0;
  cursor: pointer;
`), r => r.color);
const Dropdown = styled(Dropdown$1)(_t13 || (_t13 = _$j`
  &&& {
    position: absolute;
    padding: 8px;
    top: 3px;
    right: 10px;
    height: 20px;
    width: 33px;

    &&:hover {
      background: ${0};
    }

    &.active {
      background: ${0};
    }

    &.ui.dropdown > .dropdown.icon {
      display: none;
    }

    &.ui.dropdown > .menu > .item {
      width: 94px;
    }
  }
`), props => props.theme.color.whtite, props => props.theme.color.whtite);
const DropdownItem = styled(Dropdown$1.Item)(_t14 || (_t14 = _$j``));
const Delete = styled(Text$2)(_t15 || (_t15 = _$j`
  color: ${0};
`), props => props.theme.color.textErrorProjetSetUp);
const Item = styled(Text$2)(_t16 || (_t16 = _$j``));
const ClipFlag = styled.div(_t17 || (_t17 = _$j`
  &:after {
    content: "Clip";
  }

  border: 1px solid;
  font-size: 0.8em;
  color: rgb(21, 53, 81);
  padding: 0 10px;
  margin-left: 15px;
  background-color: rgb(237, 246, 254);
  color: #4d5473;
  border-color: #98d2fe;
  border-radius: 3px;
`));
const ClipTime = styled(Text$2)(_t18 || (_t18 = _$j`
  color: #477ae9;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  margin: 0 5px;
`));
const AddNote = styled.a(_t19 || (_t19 = _$j``));

let _$i = t => t,
  _t$i,
  _t2$c;
const Container$6 = styled.div(_t$i || (_t$i = _$i``));
const Message = styled(Text$2.Error)(_t2$c || (_t2$c = _$i`
  display: flex;
  margin-right: 30px;
  width: 100%;
`));

function ErrorHandler({
  error,
  defaultMessage = "Sorry something wrong happened.",
  revealError = false // Flag to display underneath error message
}) {
  const message = process.env.NODE_ENV === "production" && !revealError ? defaultMessage : error.message;
  if (error.networkError) {
    return React.createElement(Container$6, null, React.createElement(Message, null, "Network error. Please check your connection and try again."));
  }
  return React.createElement(Container$6, null, React.createElement(Message, null, message));
}

let _$h = t => t,
  _t$h;
const TEST_SESSION_NOTE = gql(_t$h || (_t$h = _$h`
  query TestSessionNote($id: ID!) {
    testSessionNote(id: $id) {
      id
      videoStartTime
      videoEndTime
      videoUrl
      status
      note
      noteType
      createdAt
      shareEnabled
      shareCode
      sharePassword
      createdBy {
        id
        profile {
          id
          firstName
        }
      }
    }
  }
`));

let _$g = t => t,
  _t$g,
  _t2$b,
  _t3$9,
  _t4$7,
  _t5$5,
  _t6$3,
  _t7$2;
const Button$1 = styled(Button$4)(_t$g || (_t$g = _$g``));
const ContainerText$1 = styled.div(_t2$b || (_t2$b = _$g`
  text-align: center;
  font-weight: 500;
`));
const Modal = styled(Modal$1)(_t3$9 || (_t3$9 = _$g``));
const Content$1 = styled(Modal$1.Content)(_t4$7 || (_t4$7 = _$g`
  &&& {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`));
const ContentLoader = styled(Modal$1.Content)(_t5$5 || (_t5$5 = _$g`
  &&& {
    display: flex;
    align-items: center;
    justify-content: center;
    padding-bottom: 40px;
  }
`));
const Actions = styled(Modal$1.Content)(_t6$3 || (_t6$3 = _$g`
  &&& {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`));
const Segment = styled(Segment$3)(_t7$2 || (_t7$2 = _$g`
  &:after {
    border-color: #477ae9 rgba(0, 0, 0, 0.1) rgba(0, 0, 0, 0.1)
      rgba(0, 0, 0, 0.1) !important;
  }
`));

function DownloadModal(props) {
  const {
    label,
    loading,
    onClose,
    open,
    showButton,
    submit
  } = props;
  return React.createElement(Modal, {
    size: "mini",
    open: open,
    closeOnEscape: true,
    closeOnDimmerClick: true,
    onClose: onClose
  }, React.createElement(Content$1, null, React.createElement(ContainerText$1, null, label)), loading && React.createElement(ContentLoader, null, React.createElement(Segment, {
    basic: true,
    loading: true
  })), !loading && showButton && React.createElement(Actions, null, React.createElement(Button$1, {
    onClick: submit
  }, "Download")));
}

const ERROR_MESSAGE = "We're not able to process this clip. Please contact support.";
const HANG_TIGHT_MESSAGE = "Hang tight! We're processing your clip";
const DOWNLOAD_READY_MESSAGE = "Your clip is ready";
function DownloadClip(props) {
  const {
    onClose,
    open,
    testSessionNoteId
  } = props;
  const [loading, setLoading] = useState(false);
  const [startPolling, {
    data = {},
    stopPolling
  }] = useLazyQuery(TEST_SESSION_NOTE, {
    variables: {
      id: testSessionNoteId
    },
    pollInterval: 3000
  });
  const {
    testSessionNote
  } = data;
  const showDownloadButton = testSessionNote && testSessionNote.status === "processed";
  const processingError = testSessionNote && testSessionNote.status === "error";
  const handleSubmit = useCallback(() => {
    setLoading(false);
    stopPolling();
    window.open(testSessionNote.videoUrl);
  }, [setLoading, stopPolling, testSessionNote]);
  useEffect(() => {
    if (open) {
      setLoading(true);
      startPolling();
    }
  }, [open, startPolling]);
  useEffect(() => {
    if (showDownloadButton || processingError) {
      setLoading(false);
      stopPolling();
    }
  }, [showDownloadButton, processingError, stopPolling, setLoading]);
  const getLabel = () => {
    let label = "";
    if (processingError) {
      label = ERROR_MESSAGE;
    } else {
      label = showDownloadButton ? DOWNLOAD_READY_MESSAGE : HANG_TIGHT_MESSAGE;
    }
    return label;
  };
  return React.createElement(DownloadModal, {
    label: getLabel(),
    loading: loading,
    onClose: onClose,
    open: open,
    showButton: showDownloadButton,
    submit: handleSubmit
  });
}

const HOUR = 3600;
const MINUTE = 60;
const format = value => `0${value}`.slice(-2);
const mapTime = value => {
  const minutes = Math.floor(value % HOUR / MINUTE);
  const seconds = Math.floor(value % HOUR % MINUTE);
  const hours = Math.floor(value / HOUR);
  const completeTime = `${hours}:${format(minutes)}:${format(seconds)}`;
  return {
    completeTime,
    minutes,
    seconds,
    hours
  };
};
const formatSecondsObject = value => {
  const {
    minutes,
    seconds,
    completeTime
  } = mapTime(value);
  if (value >= HOUR) {
    return completeTime;
  }
  return {
    minutes,
    seconds,
    completeTime
  };
};
const formatSeconds = value => {
  const {
    minutes,
    seconds,
    completeTime
  } = mapTime(value);
  if (value >= HOUR) {
    return completeTime;
  }
  return `${format(minutes)}:${format(seconds)}`;
};
const formatMiliseconds = value => {
  const time = value / 1000;
  return formatSeconds(time);
};

let _$f = t => t,
  _t$f,
  _t2$a,
  _t3$8,
  _t4$6,
  _t5$4,
  _t6$2,
  _t7$1,
  _t8$1,
  _t9;
const Loader = styled(Loader$4)(_t$f || (_t$f = _$f``));
styled.div(_t2$a || (_t2$a = _$f`
  position: absolute;
  background-color: #555555;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
`));
const Dialog = styled(Card)(_t3$8 || (_t3$8 = _$f`
  &&& {
    padding: 40px;
    width: fit-content;
    background-color: #fcfcfc;
    border-radius: 1px;
    max-width: ${0};
  }
`), r => r.maxWidth);
const Title = styled.h1(_t4$6 || (_t4$6 = _$f`
  color: ${0};
  font: "Roboto Medium";
  font-size: 16px;
`), props => props.color || "#ED3A4E");
const Warning = styled.p(_t5$4 || (_t5$4 = _$f`
  color: #4d5473;
  font: "Roboto Regular";
  font-size: 16px;
`));
const ContainerError = styled.div(_t6$2 || (_t6$2 = _$f`
  display: flex;
  justify-content: center;
  padding-top: 20px;
`));
const ErrorMessage = styled(Text$2.Error)(_t7$1 || (_t7$1 = _$f``));
const ButtonsArea = styled.div(_t8$1 || (_t8$1 = _$f`
  display: inline-block;
`));
const Button = styled(Button$5).attrs(props => {
  const color = props.color || "#ED3A4E";
  const backgroundColor = props.backgroundColor || "#FFFFFF";
  return {
    color,
    backgroundColor
  };
})(_t9 || (_t9 = _$f`
  &&&:hover {
    color: ${0};
    background-color: ${0};
  }
  &&& {
    text-align: center;
    color: ${0};
    background-color: ${0};
    font: "Roboto Medium";
    font-size: 15px;
    font-weight: 500;
    margin: 5pt;
    display: inline-flex;
  }
`), r => r.color, r => r.backgroundColor, r => r.color, r => r.backgroundColor);

function ConfirmDialog(props) {
  const {
    show,
    handleClose,
    handleConfirm,
    title,
    loading,
    loadingConfirmButton,
    warning,
    errorMessage,
    confirmText,
    cancelText,
    confirmTextColor,
    cancelTextColor,
    confirmTextBackgroundColor,
    cancelTextBackgroundColor,
    titleTextColor,
    maxWidth
  } = props;
  return (
    /*
      DON'T CHANGE Z-INDEX VALUE ON <Dimmer> COMPONENT BELOW
      WE WANT TO KEEP THE MODAL ON TOP OF EVERYTHING
      Z-INDEX OF PROJECT APPROVALS COMMENTS SIDEBAR IS 2147483005
    */
    React.createElement(Dimmer, {
      active: show,
      onClickOutside: handleClose,
      page: true,
      style: {
        zIndex: 2147483006
      }
    }, React.createElement(Dialog, {
      maxWidth: maxWidth
    }, React.createElement(Title, {
      color: titleTextColor
    }, title), React.createElement(Warning, null, warning), React.createElement(ButtonsArea, null, loading ? React.createElement(Loader, null) : React.createElement(React.Fragment, null, React.createElement(Button, {
      backgroundColor: confirmTextBackgroundColor || "#ED3A4E",
      color: confirmTextColor || "white",
      onClick: handleConfirm,
      loading: loadingConfirmButton
    }, confirmText), React.createElement(Button, {
      backgroundColor: cancelTextBackgroundColor,
      color: cancelTextColor || "#192C47",
      onClick: handleClose
    }, cancelText))), errorMessage && React.createElement(ContainerError, null, React.createElement(ErrorMessage, null, errorMessage))))
  );
}
ConfirmDialog.defaultProps = {
  show: false,
  warning: "",
  confirmText: "Yes",
  cancelText: "No",
  errorMessage: "",
  loading: false,
  loadingConfirmButton: false,
  maxWidth: undefined,
  confirmTextColor: undefined,
  confirmTextBackgroundColor: undefined,
  cancelTextColor: undefined,
  cancelTextBackgroundColor: undefined,
  titleTextColor: undefined
};

let _$e = t => t,
  _t$e,
  _t2$9,
  _t3$7,
  _t4$5,
  _t5$3;
const Container$5 = styled.div(_t$e || (_t$e = _$e`
  display: flex;
  flex-direction: column;
`));
const ReadOnlyContainer = styled.div(_t2$9 || (_t2$9 = _$e`
  white-space: pre-wrap;
`));
const EditButton = styled.a(_t3$7 || (_t3$7 = _$e`
  user-select: none;
`));
const Icon$1 = styled(Icon$2)(_t4$5 || (_t4$5 = _$e`
  cursor: pointer;
`));
const IconContainer = styled.div(_t5$3 || (_t5$3 = _$e`
  display: flex;
`));

function EditableRichText(props) {
  const {
    readOnly,
    readOnlyValue,
    editorState,
    hasEditButton,
    placeholder,
    userSuggestions,
    onCancel,
    onClickEdit,
    onSubmit,
    onChange
  } = props;
  const handleEditClick = e => {
    onClickEdit(e);
  };
  const handleChange = e => {
    onChange(e);
  };
  return React.createElement(Container$5, null, readOnly && React.createElement(ReadOnlyContainer, null, readOnlyValue, " ", hasEditButton && React.createElement(EditButton, {
    onClick: handleEditClick
  }, "Edit")), !readOnly && React.createElement(React.Fragment, null, React.createElement(RichTextEditor, {
    editorState: editorState,
    onChange: handleChange,
    placeholder: placeholder,
    userSuggestions: userSuggestions,
    borderless: true,
    autoResize: true
  }), React.createElement(IconContainer, null, React.createElement(Icon$1, {
    name: "check",
    color: "green",
    onClick: onSubmit
  }), React.createElement(Icon$1, {
    name: "close",
    color: "red",
    onClick: onCancel
  }))));
}
EditableRichText.defaultProps = {
  readOnly: true,
  hasEditButton: false
};

const EntityTypes = {
  MENTION: "mention"
};
const extractMentionHighlights = richText => {
  if (!richText) {
    return [];
  }
  const {
    blocks,
    entityMap
  } = richText;
  return _$B.chain(blocks)
  // Add offset to each block
  .reduce((acc, block) => {
    let offset = 0;
    let length = 0;
    const lastBlock = _$B.last(acc);
    if (lastBlock) {
      offset = lastBlock.offset;
      length = lastBlock.text.length + 1; // account for new line
    }
    const blockWithOffset = _extends({}, block, {
      offset: offset + length
    });
    return [...acc, blockWithOffset];
  }, [])
  // Offset each entity range based on block offset
  .map(block => _extends({}, block, {
    entityRanges: block.entityRanges.map(entityRange => _extends({}, entityRange, {
      offset: entityRange.offset + block.offset
    }))
  })).flatMap("entityRanges").filter(entityRange => {
    const entity = entityMap[entityRange.key];
    return entity.type === EntityTypes.MENTION;
  }).map(entityRange => ({
    id: entityRange.key,
    charStart: entityRange.offset,
    charEnd: entityRange.offset + entityRange.length - 1,
    mention: true
  })).value();
};

let _$d = t => t,
  _t$d,
  _t2$8,
  _t3$6;
const formatHexColorToRGBA = (hexColor, alpha) => {
  const rgb1 = parseInt(hexColor.substring(1, 3), 16);
  const rgb2 = parseInt(hexColor.substring(3, 5), 16);
  const rgb3 = parseInt(hexColor.substring(5, 7), 16);
  const rgba = `rgba(${rgb1}, ${rgb2}, ${rgb3}, ${alpha})`;
  return rgba;
};
styled(Text$2)(_t$d || (_t$d = _$d``));
const PieceHighlighted = styled.span(_t2$8 || (_t2$8 = _$d`
  ${0}
  ${0}

  // It must be kept on the bottom to be able to highlight the searched term
  ${0}
`), props => props.color && `
    background: ${formatHexColorToRGBA(props.color, 0.28)};
  `, props => props.mention && `
    color: #477AE9;
    border-radius: 4px;
    background-color: #DBF2FC;
  `, ({
  searchType
}) => searchType && `
    background-color: rgba(255, 234, 110,0.32);
    font-weight: 500;
  `);
const ShowMoreButton = styled.span(_t3$6 || (_t3$6 = _$d`
  cursor: pointer;
  font-weight: 500;
`));

const CELESTE = "#87CEEB";
const DARK_BLUE = "#00417E";
const DARK_BLUE_1 = "#172b4d";
const DARK_BLUE_2 = "#1c4587";
const LIGHT_BLUE = "#7f8fa4";
const MAGENTA = "#FF00FF";
const RED = "#FF0000";
const WHITE = "#FFFFFF";
const HEADING_COLOR = "#192C47";
const LINK_COLOR = "#00B7F2";
const SMALL_TEXT_COLOR = "#747474";
const WHITE_GRAY = "#E8E8E8";
const WHITE_GRAY2 = "#E9EDEF";
const LIGHT_BLUE_GREEN = "#12CEC0";
const LIGHT_BLUE_GREEN_HOVER = "#2FD3C6";
const ERROR_RED = "#9f3a38";
const BLUE_TEXT = "#122b4d";
const GREEN = "#4ad0c1";
const GREEN_2 = "#5acabe";
const GRAY_TEXT = "#5e6c84";
const DRAFT = "#4cd3fe";
const IN_PROGRESS = "#1e9cc5";
const DONE = "#1c4587";
const SIDEBAR_COLOR = "#192C47";
const SIDEBAR_SELECTED_COLOR = "#15243A";
const PROGRESS_BACKGROUND = "#101B2C";
const SHOW_SELECTED = "#407CD1";
const PROGRESS_COLOR = "#407CD1";
const TABS_COLOR = "#407CD1";
const LABEL_COLOR = "#E9EDEF";
const HOVER_LINK = "#2D62AD";
const TYPOGRAPHY = "#192C47";
const UNGROUPED_TAG_COLOR = "#FF8686";
const REACTIONS_TAG_COLOR = "#e2e2e2";

var colors = {
  __proto__: null,
  CELESTE: CELESTE,
  DARK_BLUE: DARK_BLUE,
  DARK_BLUE_1: DARK_BLUE_1,
  DARK_BLUE_2: DARK_BLUE_2,
  LIGHT_BLUE: LIGHT_BLUE,
  MAGENTA: MAGENTA,
  RED: RED,
  WHITE: WHITE,
  HEADING_COLOR: HEADING_COLOR,
  LINK_COLOR: LINK_COLOR,
  SMALL_TEXT_COLOR: SMALL_TEXT_COLOR,
  WHITE_GRAY: WHITE_GRAY,
  WHITE_GRAY2: WHITE_GRAY2,
  LIGHT_BLUE_GREEN: LIGHT_BLUE_GREEN,
  LIGHT_BLUE_GREEN_HOVER: LIGHT_BLUE_GREEN_HOVER,
  ERROR_RED: ERROR_RED,
  BLUE_TEXT: BLUE_TEXT,
  GREEN: GREEN,
  GREEN_2: GREEN_2,
  GRAY_TEXT: GRAY_TEXT,
  DRAFT: DRAFT,
  IN_PROGRESS: IN_PROGRESS,
  DONE: DONE,
  SIDEBAR_COLOR: SIDEBAR_COLOR,
  SIDEBAR_SELECTED_COLOR: SIDEBAR_SELECTED_COLOR,
  PROGRESS_BACKGROUND: PROGRESS_BACKGROUND,
  SHOW_SELECTED: SHOW_SELECTED,
  PROGRESS_COLOR: PROGRESS_COLOR,
  TABS_COLOR: TABS_COLOR,
  LABEL_COLOR: LABEL_COLOR,
  HOVER_LINK: HOVER_LINK,
  TYPOGRAPHY: TYPOGRAPHY,
  UNGROUPED_TAG_COLOR: UNGROUPED_TAG_COLOR,
  REACTIONS_TAG_COLOR: REACTIONS_TAG_COLOR
};

const reactionsMap = {
  positive: {
    icon: "/images/icon-positive.png",
    backgroundColor: "#F5FFF4",
    color: "#56E648",
    order: 1
  },
  negative: {
    icon: "/images/icon-negative.png",
    backgroundColor: "#FFF5F3",
    color: "#FF5D5D",
    order: 2
  },
  bug: {
    icon: "/images/icon-bug.png",
    backgroundColor: "#FFF6F1",
    color: "#FF9D67",
    order: 3
  },
  important: {
    icon: "/images/icon-important.png",
    backgroundColor: "#FFFDF0",
    color: "#F9DF31",
    order: 4
  },
  suggestion: {
    icon: "/images/icon-suggestion.png",
    backgroundColor: "#F4F8FF",
    color: "#5E99FF",
    order: 5
  },
  confused: {
    icon: "/images/icon-confused.png",
    backgroundColor: "#FDEFFF",
    color: "#EE7EFF",
    order: 6
  }
};

/**
 * Checks whether two highlight texts intersect each other.
 */
const intersection = (segment1, segment2) => segment1.charEnd >= segment2.charStart && segment2.charEnd >= segment1.charStart;
/**
 * It takes the entire text and if there are highlights it creates an array of segments.
 * Segments are pieces of text, which can be highlighted text or non-highlighted text.
 *
 * @example
 * input:
 *  text = "Laborum minim do Lorem amet commodo. Dolor laboris deserunt officia aliquip ut eu et."
 *
 *  tagHighlights: [{
      charStart: 17,
      charEnd:22,
      tag: { tagGroup: { color blue }}
    }]
@note
 * It caculates the new text breaking in parts to apply the highlight on the text

 @example
    output: [
      {text: "Laborum minim do ", color: null, charStart: 0, charEnd: 16}
      {text: "Lorem", color: blue, charStart: 17, charEnd:21}
      {text: " amet commodo. Dolor laboris deserunt officia aliquip ut eu et." , color: null, charStart: 22, charEnd: 84}
    ]
 *
 * @note
 * If you follow the pattern of the object used that would be
 * passing to the array. Text can be passed to highlight.
*/
const createTextSegments = (text, tagHighlights) => {
  let segments = [{
    text,
    color: null,
    charStart: 0,
    charEnd: text.length - 1,
    searchType: null
  }];
  _$B.forEach(tagHighlights, tagHighlight => {
    const {
      charStart: highlightStart,
      charEnd: highlightEnd
    } = tagHighlight;
    segments = _$B.flatMap(segments, segment => {
      const hasIntersection = intersection(segment, tagHighlight);
      if (!hasIntersection) return segment;
      const newSegments = [];
      const {
        charStart: segmentStart,
        charEnd: segmentEnd,
        color,
        searchType
      } = segment;
      if (segmentStart < highlightStart) {
        newSegments.push({
          text: text.substring(segmentStart, highlightStart),
          color,
          charStart: segmentStart,
          charEnd: highlightStart - 1
        });
      }
      const {
        tag,
        mention,
        searchType: colorSearch
      } = tagHighlight;
      const {
        tagGroup,
        tagType,
        name
      } = tag || {};
      const tagGroupColor = tagGroup && tagGroup.color;
      const newStart = Math.max(highlightStart, segmentStart);
      const newEnd = Math.min(highlightEnd, segmentEnd);
      const segmentColor = tagType === "reaction" ? reactionsMap[name].color : color || tagGroupColor || UNGROUPED_TAG_COLOR;
      newSegments.push({
        text: text.substring(newStart, newEnd + 1),
        color: segmentColor,
        charStart: newStart,
        charEnd: newEnd,
        mention,
        searchType: searchType || colorSearch
      });
      if (segmentEnd > highlightEnd) {
        newSegments.push({
          text: text.substring(highlightEnd + 1, segmentEnd + 1),
          color,
          charStart: highlightEnd + 1,
          charEnd: segmentEnd
        });
      }
      return newSegments;
    });
  });
  return segments;
};

const MAX_PREVIEW_TEXT = 425;
const MAX_NUMBER_CHARACTER_TO_SEARCH_FOR_WHITE_SPACE = 460;
function TextHighlighted(props) {
  const {
    text: fullText,
    tagHighlights,
    searchHighlights,
    richText,
    showPreview,
    onShowPreviewText
  } = props;
  const searchHighlightsType = useMemo(() => _$B.map(searchHighlights, value => _extends({}, value, {
    searchType: "searchHighlight"
  })), [searchHighlights]);
  const textLength = fullText ? fullText.length : 0;
  const isGreaterThanPreview = textLength > MAX_PREVIEW_TEXT;
  const previewText = useMemo(() => {
    let newText = fullText;
    let strPosition = MAX_PREVIEW_TEXT;
    for (; strPosition < textLength; strPosition += 1) {
      if (fullText.charAt(strPosition) === " ") {
        newText = fullText.substring(0, strPosition);
        break;
      }
      if (strPosition === MAX_NUMBER_CHARACTER_TO_SEARCH_FOR_WHITE_SPACE) {
        newText = fullText.substring(0, strPosition);
        break;
      }
    }
    return newText;
  }, [fullText, textLength]);
  const text = useMemo(() => isGreaterThanPreview && !showPreview && onShowPreviewText ? previewText : fullText, [fullText, isGreaterThanPreview, onShowPreviewText, previewText, showPreview]);
  const orderedHighlights = useMemo(() => _$B.chain(tagHighlights).orderBy(["charStart"]).concat(extractMentionHighlights(richText)).concat(searchHighlightsType).value(), [tagHighlights, richText, searchHighlightsType]);
  const segmentList = useMemo(() => createTextSegments(text, orderedHighlights), [orderedHighlights, text]);
  return React.createElement(React.Fragment, null, _$B.map(segmentList, segment => React.createElement(PieceHighlighted, {
    "data-char-start": segment.charStart,
    key: segment.charStart,
    charStart: segment.charStart,
    color: segment.color,
    mention: segment.mention,
    className: "piece-highlighted",
    searchType: segment.searchType
  }, segment.text)), isGreaterThanPreview && onShowPreviewText && React.createElement(React.Fragment, null, React.createElement("br", null), React.createElement(ShowMoreButton, {
    onClick: onShowPreviewText
  }, !showPreview && "Show more", showPreview && "Show less")));
}
TextHighlighted.defaultProps = {
  searchHighlights: [],
  richText: undefined,
  showPreview: false,
  onShowPreviewText: null
};

let _$c = t => t,
  _t$c;
const Container$4 = styled.div(_t$c || (_t$c = _$c`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  max-width: 100%;
  margin-top: ${0};
  margin-bottom: ${0};

  & > div {
    margin-top: 5px;

    &:not(:last-child) {
      margin-right: 10px;
    }
  }
`), props => props.$marginTop, props => props.$marginBottom);

let _$b = t => t,
  _t$b,
  _t2$7,
  _t3$5,
  _t4$4,
  _t5$2,
  _t6$1,
  _t7,
  _t8;
const CloseButton = styled.div(_t$b || (_t$b = _$b`
  display: none;
  position: absolute;
  background-color: #696969;
  width: 16px;
  height: 16px;
  border-radius: 8px;
  top: -8px;
  right: -8px;
  cursor: pointer;

  &:hover {
    background-color: #888888;
  }

  &:active {
    background-color: #696969;
  }
`));
const Container$3 = styled.div(_t2$7 || (_t2$7 = _$b`
  background-color: ${0};
  padding: 2px 4px;
  border-radius: 3px;
  position: relative;
  display: flex;
  flex-direction: row;

  &:hover ${0} {
    display: block;
  }
`), props => props.color, CloseButton);
const ContainerText = styled.div(_t3$5 || (_t3$5 = _$b`
  display: flex;
`));
const Text = styled(Text$2.Span)(_t4$4 || (_t4$4 = _$b`
  color: ${0};
  user-select: ${0};
  font-weight: 500;
  white-space: nowrap;
  display: inline-block;
  vertical-align: bottom;
  line-height: 20px;
`), props => props.theme.color.white, props => props.selectable ? "text" : "none");
const TagLabel = styled(Text)(_t5$2 || (_t5$2 = _$b`
  max-width: 216px;
  overflow: hidden;
  text-overflow: ellipsis;
`));
const TagCount = styled(Text)(_t6$1 || (_t6$1 = _$b`
  margin-left: 4px;
`));
const Icon = styled(Icon$2)(_t7 || (_t7 = _$b`
  &&& {
    color: white;
    font-size: 11px;
    width: 13px;
    height: 13px;
    position: absolute;
    top: -1px;
    right: -2px;
  }
`));
const Reaction = styled(Image)(_t8 || (_t8 = _$b`
  display: inline-block;
  height: 19px;
  margin-right: 5px;
`));

function Tag(props) {
  const {
    text,
    number,
    color,
    selectable,
    type,
    onClose
  } = props;
  let displayText = text;
  let colorReaction = color;
  if (type === "reaction") {
    displayText = _$B.capitalize(displayText);
    if (!colorReaction) {
      colorReaction = reactionsMap[text].color;
    }
  }
  const {
    container
  } = useIfScope();
  return React.createElement(Container$3, {
    color: color || colorReaction || UNGROUPED_TAG_COLOR
  }, type === "reaction" && React.createElement(Reaction, {
    src: reactionsMap[text].icon
  }), React.createElement(ContainerText, null, React.createElement(TagLabel, {
    numberOfLines: 1,
    selectable: selectable
  }, displayText), number && React.createElement(TagCount, {
    selectable: selectable
  }, number)), container(onClose && React.createElement(CloseButton, {
    onClick: onClose
  }, React.createElement(Icon, {
    name: "x"
  }))));
}
Tag.defaultProps = {
  number: null,
  selectable: false,
  type: "normal",
  onClose: null
};

function TagListFromTagHighlights(props) {
  const {
    tagHighlights = [],
    marginTop = "",
    marginBottom = "",
    onRemoveTagHighlight
  } = props;
  return React.createElement(Container$4, {
    "$marginTop": marginTop,
    "$marginBottom": marginBottom
  }, tagHighlights.map(tagHighlight => {
    const {
      id: tagHighlightId,
      tag: {
        name,
        count,
        tagGroup,
        tagType
      }
    } = tagHighlight;
    const {
      color = UNGROUPED_TAG_COLOR
    } = tagGroup || {};
    return React.createElement(Tag, {
      key: tagHighlightId,
      text: name,
      number: count,
      color: tagType === "reaction" ? reactionsMap[name].color : color,
      type: tagType,
      selectable: true,
      onClose: onRemoveTagHighlight ? () => onRemoveTagHighlight(tagHighlight) : null
    });
  }));
}

function Note(props) {
  const {
    onClickShare,
    onConfirm,
    onCurrentTime,
    onDownloadClip,
    onNoteSelected,
    onSubmit,
    testSessionNote,
    onRemoveTagHighlight,
    userSuggestions,
    removeTag,
    removeClip,
    readOnlyScope
  } = props;
  const {
    id: testSessionNoteId,
    createdBy,
    note,
    noteType,
    noteRichText,
    videoEndTime,
    videoStartTime,
    tagHighlights
  } = testSessionNote;
  const [readOnly, setReadOnly] = useState(true);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showLink, setShowLink] = useState(false);
  const [editorState, setEditorState] = useState(createEmptyState());
  const {
    container
  } = useIfScope();
  const {
    scope
  } = useWebScope();
  useEffect(() => {
    const show = _$B.isEmpty(note) && readOnly;
    setShowLink(show);
  }, [note, readOnly]);
  const handleOpenDialog = () => {
    setShowConfirm(true);
  };
  const handleConfirm = () => {
    setShowConfirm(false);
    onConfirm();
  };
  const handleClose = () => {
    setShowConfirm(false);
  };
  const handleTextChange = newValue => {
    setEditorState(newValue);
  };
  const handleEdit = () => {
    setEditorState(createEditorStateFromNote(testSessionNote));
    setReadOnly(false);
  };
  const handleCancel = () => {
    setReadOnly(true);
  };
  const handleSubmit = () => {
    const plaintText = convertToPlainText(editorState);
    if (!_$B.isEmpty(plaintText)) {
      setReadOnly(true);
      onSubmit(editorState);
    } else {
      handleCancel();
    }
  };
  const handleShare = () => {
    onClickShare(testSessionNote);
  };
  const handleClickOnTime = currentTime => {
    onCurrentTime(currentTime);
  };
  const handleAddNote = () => {
    setShowLink(false);
    handleEdit();
  };
  const handleRemoveTagHighlight = (...args) => {
    onRemoveTagHighlight(testSessionNoteId, ...args);
  };
  const noteTypeFormatted = _$B.toString(noteType);
  const startTimeFormatted = formatSeconds(videoStartTime);
  return /*#__PURE__*/React.createElement(Segment$1, {
    dataId: readOnly ? testSessionNoteId : "",
    dataEntity: "TestSessionNote"
  }, container(/*#__PURE__*/React.createElement(Dropdown, {
    item: true,
    trigger: /*#__PURE__*/React.createElement(ImageIcon, {
      src: "/images/dots-icon.png",
      alt: "Playbook"
    })
  }, /*#__PURE__*/React.createElement(Dropdown.Menu, {
    direction: "left"
  }, noteType === "clip" && !removeClip && !readOnlyScope && /*#__PURE__*/React.createElement(DropdownItem, {
    onClick: () => onNoteSelected(testSessionNote)
  }, /*#__PURE__*/React.createElement(Item, null, "Edit Clip")), !readOnlyScope && /*#__PURE__*/React.createElement(DropdownItem, {
    onClick: handleEdit
  }, /*#__PURE__*/React.createElement(Item, null, showLink ? "Add Note" : "Edit Note")), noteType === "clip" && /*#__PURE__*/React.createElement(DropdownItem, {
    onClick: () => onDownloadClip(testSessionNote)
  }, /*#__PURE__*/React.createElement(Item, null, "Download")), noteType === "clip" && !removeClip && /*#__PURE__*/React.createElement(DropdownItem, {
    onClick: handleShare
  }, /*#__PURE__*/React.createElement(Item, null, "Share")), !readOnlyScope && /*#__PURE__*/React.createElement(DropdownItem, {
    onClick: handleOpenDialog
  }, /*#__PURE__*/React.createElement(Delete, null, "Delete"))))), /*#__PURE__*/React.createElement(ConfirmDialog, {
    show: showConfirm,
    title: `Delete ${noteTypeFormatted}`,
    warning: "Warning: This cannot be undone",
    confirmText: `Yes, delete ${noteTypeFormatted}`,
    cancelText: `Cancel, keep ${noteTypeFormatted}`,
    handleConfirm: handleConfirm,
    handleClose: handleClose
  }), /*#__PURE__*/React.createElement(NoteHeaderContainer, null, /*#__PURE__*/React.createElement(NoteName, null, createdBy.profile.firstName), noteType === "note" && !scope && /*#__PURE__*/React.createElement(NoteName, {
    onClick: () => handleClickOnTime(videoStartTime)
  }, `@${startTimeFormatted}`), noteType === "note" && scope === "meeting" && /*#__PURE__*/React.createElement(NoteTimeStampsSecondary, null, `@${startTimeFormatted}`), noteType === "clip" && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ClipTime, {
    onClick: () => handleClickOnTime(videoStartTime)
  }, startTimeFormatted), "-", /*#__PURE__*/React.createElement(ClipTime, {
    onClick: () => handleClickOnTime(videoEndTime)
  }, formatSeconds(videoEndTime)), /*#__PURE__*/React.createElement(ClipFlag, null))), /*#__PURE__*/React.createElement(ContainerNote, null, !showLink ? /*#__PURE__*/React.createElement(EditableRichText, {
    readOnly: readOnly,
    readOnlyValue: /*#__PURE__*/React.createElement(TextHighlighted, {
      tagHighlights: tagHighlights,
      text: note,
      richText: noteRichText && JSON.parse(noteRichText)
    }),
    editorState: editorState,
    userSuggestions: userSuggestions,
    onClickEdit: handleEdit,
    onSubmit: handleSubmit,
    onChange: handleTextChange,
    onCancel: handleCancel,
    showButtons: true
  }) : /*#__PURE__*/React.createElement(AddNote, {
    onClick: handleAddNote
  }, "Click to add a note")), !removeTag && /*#__PURE__*/React.createElement(TagListFromTagHighlights, {
    tagHighlights: tagHighlights,
    onRemoveTagHighlight: handleRemoveTagHighlight
  }));
}
Note.propTypes = {
  noteRichText: PropTypes.string,
  userSuggestions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string
  })).isRequired,
  // to remove some features from web meeting
  removeTag: PropTypes.bool.isRequired,
  removeClip: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onRemoveTagHighlight: PropTypes.func.isRequired,
  onCurrentTime: PropTypes.func.isRequired,
  onClickShare: PropTypes.func.isRequired,
  onNoteSelected: PropTypes.func.isRequired,
  onDownloadClip: PropTypes.func.isRequired,
  testSessionNote: PropTypes.shape({
    id: PropTypes.string.isRequired,
    noteRichText: PropTypes.string,
    createdBy: PropTypes.shape({
      profile: PropTypes.shape({
        id: PropTypes.string.isRequired,
        firstName: PropTypes.string.isRequired
      })
    }),
    note: PropTypes.string.isRequired,
    noteType: PropTypes.string.isRequired,
    videoEndTime: PropTypes.number,
    videoStartTime: PropTypes.number.isRequired,
    tagHighlights: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      charStart: PropTypes.number.isRequired,
      charEnd: PropTypes.number.isRequired,
      tag: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        count: PropTypes.number.isRequired,
        tagGroup: PropTypes.shape({
          id: PropTypes.string.isRequired,
          color: PropTypes.string.isRequired
        })
      }).isRequired
    }))
  }).isRequired,
  readOnlyScope: PropTypes.bool.isRequired
};

function Notes(props) {
  const {
    onCurrentTime = time => time,
    onNoteSelected = item => item,
    testSessionId,
    project,
    removeTag = false,
    removeClip = false,
    readOnly = false,
    defaultUrl
  } = props;
  const {
    scope
  } = useWebScope();
  const {
    currentTime
  } = useMedia();
  const scrollParentRef = useRef(null);
  const [noteId, setNoteId] = useState("");
  const [showShare, setShowShare] = useState(false);
  const [sharedClip, setSharedClip] = useState({});
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [editorState, setEditorState] = useState(createEmptyState());
  const {
    data: companyMembers
  } = useCompanyMembers(project.company.id);
  const variables = {
    testSessionId,
    limit: LIMIT,
    offset: 0,
    order: "created_at DESC"
  };
  const [createTestSessionNote] = useMutation(CREATE_TEST_SESSION_NOTE, {
    refetchQueries: [{
      query: TEST_SESSION_NOTES,
      variables
    }]
  });
  const [updateTestSessionNote] = useMutation(UPDATE_TEST_SESSION_NOTE);
  const [deleteTestSessionNote] = useMutation(DELETE_TEST_SESSION_NOTE, {
    refetchQueries: [{
      query: TEST_SESSION_NOTES,
      variables
    }]
  });
  const [deleteTagHighlight] = useMutation(DELETE_TAG_HIGHLIGHT);
  const {
    container
  } = useIfScope();
  const attributes = {
    fetchPolicy: "network-only",
    variables,
    skip: !testSessionId
  };
  const {
    error,
    data = {},
    fetchMore,
    hasMore
  } = usePaginatedQuery(TEST_SESSION_NOTES, attributes, {
    dataField: "testSessionNotes"
  });
  const {
    testSessionNotes = []
  } = data;
  useEffect(() => {
    if (!_$B.isEmpty(noteId)) {
      deleteTestSessionNote({
        variables: {
          noteId
        }
      });
    }
  }, [noteId, deleteTestSessionNote]);
  const getCurrentTime = () => {
    if (typeof currentTime === "function") return currentTime();
    return currentTime;
  };
  const handleCreateNote = () => {
    createTestSessionNote({
      variables: {
        testSessionId,
        attributes: {
          note: convertToPlainText(editorState),
          noteRichText: JSON.stringify(convertToRichText(editorState)),
          noteType: "note",
          videoStartTime: Math.trunc(getCurrentTime() || 0)
        }
      }
    });
  };
  const handleUpdateNote = (value, newNoteId) => {
    updateTestSessionNote({
      variables: {
        id: newNoteId,
        attributes: {
          note: convertToPlainText(value),
          noteRichText: JSON.stringify(convertToRichText(value))
        }
      }
    });
  };
  const handleKeyDown = command => {
    if (command === EDITOR_STATUS.SUBMIT) {
      const newEditorState = pushStateChange(editorState, "");
      setEditorState(newEditorState);
      handleCreateNote();
      return EDITOR_STATUS.HANDLED;
    }
    return EDITOR_STATUS.NOT_HANDLED;
  };
  const handleTextChange = value => {
    if (readOnly) return;
    setEditorState(value);
  };
  const handleClickShare = item => {
    setShowShare(true);
    setSharedClip(item);
  };
  const handleCloseShare = () => {
    setShowShare(false);
  };
  const handleDownloadClip = testSessionNote => {
    setSelectedNote(testSessionNote);
    if (testSessionNote.videoUrl && testSessionNote.status === "processed") {
      const url = testSessionNote.videoUrl;
      setSelectedNote(null);
      window.open(url);
    } else {
      setDownloadModalOpen(true);
    }
  };
  const handleDeleteNote = newNoteId => {
    setNoteId(newNoteId);
  };
  const handleRemoveTagHighlight = (removedNoteId, tagHighlight) => {
    deleteTagHighlight({
      variables: {
        ids: [tagHighlight.id]
      },
      update: updateNoteTagHighlightsDeleted(removedNoteId, tagHighlight)
    });
  };
  const getReadOnlyAttrs = () => {
    if (readOnly) {
      return {
        readOnly: true,
        onChange: handleTextChange
      };
    }
    return {
      userSuggestions: companyMembers,
      onChange: handleTextChange,
      onKeyDown: handleKeyDown,
      submitOnEnter: true
    };
  };
  return React.createElement(ContainerNotes, null, scope === "meeting" && React.createElement("div", {
    style: {
      paddingRight: "20px",
      paddingLeft: "20px"
    }
  }, React.createElement(SegmentTextArea, null, React.createElement(RichTextEditor, _extends({
    editorState: editorState,
    placeholder: "Type and hit enter to add a note"
  }, getReadOnlyAttrs())))), React.createElement(ScrollBarContainer, null, React.createElement(ShareClipDialog, {
    note: sharedClip,
    show: showShare,
    onClose: handleCloseShare,
    defaultUrl: defaultUrl
  }), React.createElement(DownloadClip, {
    open: downloadModalOpen,
    onClose: () => setDownloadModalOpen(false),
    testSessionNoteId: selectedNote ? selectedNote.id : null
  }), !scope && !readOnly && container(React.createElement(SegmentTextArea, null, React.createElement(RichTextEditor, _extends({
    editorState: editorState,
    placeholder: "Type and hit enter to add a note"
  }, getReadOnlyAttrs())))), error && React.createElement(ErrorHandler, {
    error: error,
    defaultMessage: "Error while retrieving the Notes & Clips."
  }), React.createElement(InfiniteScrollComponent, {
    hasMore: hasMore,
    onLoadMore: fetchMore,
    getScrollParent: () => scrollParentRef.current
  }, testSessionNotes && testSessionNotes.map(note => React.createElement(Note, {
    key: note.id,
    testSessionNote: note,
    userSuggestions: companyMembers,
    onCurrentTime: onCurrentTime,
    onNoteSelected: onNoteSelected,
    removeClip: removeClip,
    removeTag: removeTag,
    onClickShare: handleClickShare,
    onSubmit: value => {
      handleUpdateNote(value, note.id);
    },
    onConfirm: () => handleDeleteNote(note.id),
    onDownloadClip: () => handleDownloadClip(note),
    onRemoveTagHighlight: handleRemoveTagHighlight,
    readOnlyScope: readOnly
  })))));
}

let _$a = t => t,
  _t$a;
const HighlightedSpan = styled.span(_t$a || (_t$a = _$a`
  white-space: pre-wrap;
  color: ${0};
  background-color: ${0};
`), props => props.fontColor || "#4D5473", props => props.bgColor || "transparent");

// this is a simple highlighter component
// any duplicated words in a text prop is going to be highlighted
function TextHighlighter(props) {
  const {
    text,
    highlightWords,
    fontColor,
    bgColor
  } = props;
  const parts = text.split(new RegExp(`\\b(${highlightWords.join("|")})\\b`, "gi"));
  return parts.map(part => {
    if (highlightWords.includes(part)) {
      return React.createElement(HighlightedSpan, {
        fontColor: fontColor,
        bgColor: bgColor
      }, part);
    }
    return React.createElement(HighlightedSpan, null, part);
  });
}
TextHighlighter.defaultProps = {
  fontColor: "#477ae9",
  bgColor: "#dbf2fc"
};

let _$9 = t => t,
  _t$9,
  _t2$6,
  _t3$4,
  _t4$3;
const StyledInput = styled(Input$2)(_t$9 || (_t$9 = _$9`
  &&&.ui.input input {
    text-align: center;
    width: 30px;
    padding: 0;
    display: flex;
    height: 30px;
    border-top: 1px solid #d4d4d4;
    border-bottom: 1px solid #d4d4d4;
    border-left: 0;
    border-right: 0;
    border-radius: 0;
  }
`));
const StyledMinusButton = styled(Button$5)(_t2$6 || (_t2$6 = _$9`
  &&&.ui.icon.button, .ui.icon.buttons .button {
    width: 20px;
    margin: 0;
    padding: 0;
    background-color: white;
    border-top: 1px solid #d4d4d4;
    border-bottom: 1px solid #d4d4d4;
    border-left: 1px solid #d4d4d4;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    height: 30px;
  }
`));
const StyledPlusButton = styled(Button$5)(_t3$4 || (_t3$4 = _$9`
  &&&.ui.icon.button, .ui.icon.buttons .button {
    width: 20px;
    margin: 0;
    padding: 0;
    background-color: white;
    border-top: 1px solid #d4d4d4;
    border-bottom: 1px solid #d4d4d4;
    border-right: 1px solid #d4d4d4;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    height: 30px;
  }
`));
const Container$2 = styled.div(_t4$3 || (_t4$3 = _$9`
  display: flex;
  align-items: center;
  margin: 0 5px 0 5px;
`));

function InputNumberField(props) {
  const {
    min,
    max,
    maxLength,
    setValue,
    value
  } = props;
  const handleIncrement = () => {
    if (value < max) {
      setValue(value + 1);
    }
  };
  const handleDecrement = () => {
    if (value > min) {
      setValue(value - 1);
    }
  };
  const handleChange = event => {
    setValue(toInteger(event.target.value) || 0);
  };
  return React.createElement(Container$2, null, React.createElement(StyledMinusButton, {
    icon: "minus",
    size: "mini",
    onClick: handleDecrement
  }), React.createElement(StyledInput, {
    value: value,
    maxLength: maxLength,
    min: min,
    max: max,
    onChange: handleChange
  }), React.createElement(StyledPlusButton, {
    icon: "plus",
    size: "mini",
    onClick: handleIncrement
  }));
}

var AUTHENTICATION_SCOPES;
(function (AUTHENTICATION_SCOPES) {
  AUTHENTICATION_SCOPES["USER"] = "user";
  AUTHENTICATION_SCOPES["SHARE_PROJECT"] = "project";
  AUTHENTICATION_SCOPES["SHARE_CLIP"] = "clip";
  AUTHENTICATION_SCOPES["SHARE_HIGHLIGHT_REEL"] = "highlight-reel";
  AUTHENTICATION_SCOPES["REPORT"] = "report";
  AUTHENTICATION_SCOPES["PUBLIC"] = "public";
  AUTHENTICATION_SCOPES["TESTER"] = "tester";
  AUTHENTICATION_SCOPES["LIVESTREAM"] = "livestream";
})(AUTHENTICATION_SCOPES || (AUTHENTICATION_SCOPES = {}));
var AUTHENTICATION_SCOPES$1 = AUTHENTICATION_SCOPES;

const ALL_SCOPES = Object.values(AUTHENTICATION_SCOPES$1).filter(x => x !== AUTHENTICATION_SCOPES$1.PUBLIC);
function IfScope(props) {
  const {
    children,
    scope,
    user,
    shareProject,
    tester
  } = props;
  const render = () => {
    if (_$B.isFunction(children)) {
      return children();
    }
    return children;
  };
  if (user && scope === AUTHENTICATION_SCOPES$1.USER) {
    return render();
  }
  if (shareProject && scope === AUTHENTICATION_SCOPES$1.SHARE_PROJECT) {
    return render();
  }
  if (tester && scope === AUTHENTICATION_SCOPES$1.TESTER) {
    return render();
  }
  return null;
}
IfScope.defaultProps = {
  user: false,
  shareProject: false,
  tester: false
};

let _$8 = t => t,
  _t$8,
  _t2$5;
const Header$1 = styled(Header$2).attrs(props => _extends({}, props))(_t$8 || (_t$8 = _$8``));
Header$1.Danger = styled(Header$1)(_t2$5 || (_t2$5 = _$8`
  &.ui.header {
    font-size: 16px;
    color: ${0};
  }
`), props => props.theme.color.warningHeader);

let _$7 = t => t,
  _t$7;
const UpgradeButton = styled(Button$4)(_t$7 || (_t$7 = _$7`
  &.ui.button {
    background: ${0};
    border-radius: 5px;
    color: ${0};
    cursor: ${0};
    font-size: 12px;
    font-weight: 500;
    margin: ${0};
    padding: 5px;
    box-shadow: unset;

    :hover {
      background: ${0};
      color: ${0};
    }
  }
`), ({
  theme
}) => theme.color.comingSoon, ({
  theme
}) => theme.color.buttonInvertedText, ({
  pointer
}) => pointer ? "pointer" : "not-allowed", ({
  noMargin
}) => noMargin ? 0 : "20px", ({
  theme
}) => theme.color.comingSoon, ({
  theme
}) => theme.color.buttonInvertedText);

let _$6 = t => t,
  _t$6;
const Checkbox$1 = styled(Checkbox$2)(_t$6 || (_t$6 = _$6`
  &.ui.checkbox {
    label {
      color: ${0};
      font-size: ${0};
    }
    margin: 0px;
    padding: 0px;
  }
`), props => props.theme.color.checkbox, props => props.theme.fontSize.checkboxText);

let _$5 = t => t,
  _t$5,
  _t2$4,
  _t3$3,
  _t4$2,
  _t5$1;
const Container$1 = styled.div(_t$5 || (_t$5 = _$5`
  position: relative;
`));
const CheckBoxLabel = styled.label(_t2$4 || (_t2$4 = _$5`
  position: absolute;
  top: 0;
  left: 0;
  width: 36px;
  height: 18px;
  border-radius: 15px;
  background: #ccc;
  cursor: ${0};
  transition: 0.4s;
  &::after {
    content: "";
    display: block;
    border-radius: 50%;
    width: 12px;
    height: 12px;
    margin: 3px;
    background: #ffffff;
    transition: 0.4s;
  }
`), props => props.disabled ? "normal" : "pointer");
const CheckBox = styled.input(_t3$3 || (_t3$3 = _$5`
  opacity: 0;
  z-index: 0;
  border-radius: 15px;
  width: 36px;
  transition: 0.4s;
  height: 18px;
  &:checked + ${0} {
    background: #477ae9;
    &::after {
      content: "";
      display: block;
      border-radius: 50%;
      width: 12px;
      height: 12px;
      margin-left: 21px;
      transition: 0.4s;
    }
  }
`), CheckBoxLabel);
const ContainerToggle = styled.div(_t4$2 || (_t4$2 = _$5`
  display: flex;
  align-items: center;
  margin: ${0};
`), props => props.marginVertical ? "14px 0px" : "5px;");
const TextToggle = styled(Text$2)(_t5$1 || (_t5$1 = _$5`
  margin-left: 10px;
  font-size: 12px;
`));

function Toggle({
  checked,
  id,
  onChange,
  disabled,
  ariaLabel,
  ariaLabelBy
}) {
  return React.createElement(Container$1, null, React.createElement(CheckBox, {
    checked: checked,
    id: id,
    onChange: onChange,
    type: "checkbox",
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelBy
  }), React.createElement(CheckBoxLabel, {
    disabled: disabled,
    htmlFor: id
  }));
}
Toggle.defaultProps = {
  checked: undefined,
  onChange: undefined,
  disabled: false,
  ariaLabel: undefined,
  ariaLabelBy: undefined
};

let _$4 = t => t,
  _t$4,
  _t2$3;
const Label$2 = styled(Text$2)(_t$4 || (_t$4 = _$4`
  font-size: ${0};
  font-weight: ${0};
  margin-bottom: 5px;

  color: ${0};
`), props => props.fontSize ? props.fontSize : "18px", props => props.weight === "500" ? props.weight : "400", ({
  error,
  color
}) => error ? ERROR_RED : color);
const TextArea = styled(Form.TextArea)(_t2$3 || (_t2$3 = _$4`
  &&& {
    textarea {
      display: flex;
      padding: 10px;
      height: ${0};
      min-width: ${0};
      color: ${0};
      border-color: #dededf;
      resize: vertical;

      &:focus {
        border-color: #477ae9;
        color: rgba(0, 0, 0, 0.8);
      }
    }
  }
`), props => props.height ? props.height : "66px", props => props.minwidth ? props.minwidth : "0px", GRAY_TEXT);

function TextAreaFormField(props) {
  const {
    field,
    form,
    labelFontSize,
    labelWeight,
    labelColor,
    labelText,
    content,
    rows,
    height,
    minWidth,
    placeholder,
    type,
    onChange,
    onBlur,
    audience
  } = props;
  return React.createElement(Form.Field, null, React.createElement(Label$2, {
    error: _$B.get(form.errors, field.name) && _$B.get(form.touched, field.name),
    fontSize: labelFontSize,
    weight: labelWeight,
    color: labelColor
  }, labelText, content && React.createElement(Popup, {
    trigger: React.createElement(Icon$2, {
      name: "info circle"
    }),
    content: content
  })), React.createElement(TextArea, _extends({
    rows: rows,
    margin: "dense",
    height: height,
    minwidth: minWidth,
    placeholder: placeholder,
    type: type || "text"
  }, field, {
    onChange: onChange || field.onChange,
    onBlur: onBlur || field.onBlur,
    audience: audience,
    error: _$B.get(form.errors, field.name) && _$B.get(form.touched, field.name)
  })));
}
TextAreaFormField.defaultProps = {
  audience: undefined,
  type: "text",
  placeholder: undefined,
  minWidth: undefined,
  height: undefined,
  labelText: undefined,
  content: undefined,
  labelFontSize: undefined,
  labelWeight: undefined,
  labelColor: undefined,
  rows: 2,
  onChange: undefined,
  onBlur: undefined
};
var index$1 = React.memo(TextAreaFormField);

let _$3 = t => t,
  _t$3,
  _t2$2,
  _t3$2,
  _t4$1;
const Label$1 = styled(Text$2)(_t$3 || (_t$3 = _$3`
  font-size: ${0};
  font-weight: ${0};
  margin-bottom: 5px;

  color: ${0};
`), props => props.fontSize ? props.fontSize : "18px", props => props.weight === "500" ? props.weight : "400", ({
  error,
  color
}) => error ? ERROR_RED : color);
const TextInfo = styled(Text$2)(_t2$2 || (_t2$2 = _$3``));
const FormInput = styled(Form.Input).attrs(({
  autoComplete
}) => ({
  autoComplete
}))(_t3$2 || (_t3$2 = _$3`
  &&& {
    ${0}
    ${0}
  }
`), ({
  audience,
  minwidth
}) => audience && `
      input {
        min-width: ${minwidth || 0};
        color: ${GRAY_TEXT};
      }
      input:focus {
        color: #555555;
      }
    `, ({
  widthsize
}) => widthsize && `
      input {
        width: ${widthsize};
      }
      .ui.input {
        width: ${widthsize};
      }
    `);
const FormField = styled(Form.Field)(_t4$1 || (_t4$1 = _$3`
  ${0}
`), ({
  widthsize
}) => widthsize && `width: ${widthsize};`);

function InputFormField(props) {
  const {
    field,
    form: {
      errors,
      touched,
      setFieldValue
    },
    onChangeUrl,
    onChange,
    ariaLabel,
    ariaDescribedBy,
    ariaRequired,
    ariaInvalid,
    widthsize,
    labelText,
    labelFontSize,
    labelWeight,
    content,
    info,
    icon,
    type,
    placeholder,
    onBlur,
    onKeyUp,
    audience,
    readOnly,
    width,
    minWidth,
    disabled,
    fluid,
    onFocus,
    autoComplete,
    min,
    max
  } = props;
  const handleChange = (e, data) => {
    if (onChangeUrl) {
      onChangeUrl(e.target.value);
      setFieldValue(field.name, e.target.value);
      return;
    }
    if (onChange) {
      onChange(e, data);
      return;
    }
    field.onChange(e, data);
  };
  return React.createElement(FormField, {
    widthsize: widthsize
  }, labelText && React.createElement(Label$1, {
    error: _$B.get(errors, field.name) && _$B.get(touched, field.name),
    fontSize: labelFontSize,
    weight: labelWeight
  }, labelText, content && React.createElement(Popup, {
    trigger: React.createElement(Icon$2, {
      name: "info circle"
    }),
    content: content
  })), info && React.createElement(TextInfo, null, info), React.createElement(FormInput, _extends({
    autoComplete: autoComplete,
    margin: "dense",
    min: min,
    max: max,
    onKeyUp: onKeyUp,
    icon: icon,
    placeholder: placeholder,
    type: type || "text"
  }, field, {
    onChange: handleChange,
    onBlur: error => {
      if (onBlur) {
        return onBlur(error);
      }
      if (field.onBlur) {
        return field.onBlur(error);
      }
      return {};
    },
    onFocus: () => {
      if (onFocus) onFocus();
    },
    width: width,
    audience: audience,
    readOnly: readOnly,
    widthsize: widthsize,
    minwidth: minWidth,
    disabled: disabled,
    fluid: fluid,
    error: _$B.get(errors, field.name) && _$B.get(touched, field.name),
    "aria-label": ariaLabel,
    "aria-describedby": ariaDescribedBy,
    "aria-required": ariaRequired,
    "aria-invalid": ariaInvalid
  })));
}
InputFormField.defaultProps = {
  labelText: "",
  info: "",
  audience: undefined,
  readOnly: false,
  disabled: false,
  fluid: false,
  minWidth: undefined,
  width: undefined,
  widthsize: undefined,
  icon: undefined,
  placeholder: "",
  onChange: undefined,
  labelFontSize: undefined,
  labelWeight: undefined,
  content: undefined,
  type: undefined,
  onKeyUp: undefined,
  onBlur: undefined,
  onChangeUrl: null,
  onFocus: null,
  onClick: null,
  ariaLabel: undefined,
  ariaDescribedBy: undefined,
  ariaRequired: undefined,
  ariaInvalid: undefined
};
var index = React.memo(InputFormField);

function offsetLimitPagination(keyArgs) {
  return {
    keyArgs,
    merge(existing, incoming, {
      args
    }) {
      const merged = existing ? existing.slice(0) : [];
      if (args) {
        // Assume an offset of 0 if args.offset omitted.
        const {
          offset = 0
        } = args;
        for (let i = 0; i < incoming.length; i += 1) {
          merged[offset + i] = incoming[i];
        }
        // In case it we are updating the cache when deleting,
        // we should remove existing items in the cache when merging.
        if (offset === 0 && incoming.length < merged.length) {
          merged.splice(incoming.length - 1, merged.length - incoming.length);
        }
      } else {
        const error = new Error("Missing pagination arguments in offsetLimitPagination.");
        captureException("OFFSET_LIMIT_PAGINATION_FAILED", error);
        // It's unusual (probably a mistake) for a paginated field not
        // to receive any arguments, so you might prefer to throw an
        // exception here, instead of recovering by appending incoming
        // onto the existing array.
        throw new Error("Missing pagination arguments in offsetLimitPagination.");
      }
      return merged;
    }
  };
}

/**
 * Validates the quality of a given video file.
 *
 * This function creates a video element, loads the metadata of the video file,
 * and determines the quality based on the video's resolution.
 *
 * @param {File} file - The video file to be validated.
 * @returns {Promise<string>} - A promise that resolves to a string representing the video quality.
 *
 * Possible quality values:
 * - '4K' for resolutions 3840x2160 and above
 * - '2K' for resolutions 2560x1440 and above
 * - 'Full HD' for resolutions 1920x1080 and above
 * - 'HD' for resolutions 1280x720 and above
 * - 'SD' for resolutions below 1280x720
 *
 * The promise is rejected with an error if the video file is invalid.
 */
const validateVideoQuality = file => new Promise((resolve, reject) => {
  const video = document.createElement("video");
  video.preload = "metadata";
  let quality = "";
  video.onloadedmetadata = () => {
    window.URL.revokeObjectURL(video.src);
    const {
      videoWidth,
      videoHeight
    } = video || {};
    if (videoWidth >= 3840 && videoHeight >= 2160) {
      quality = "4K";
    } else if (videoWidth >= 2560 && videoHeight >= 1440) {
      quality = "2K";
    } else if (videoWidth >= 1920 && videoHeight >= 1080) {
      quality = "Full HD";
    } else if (videoWidth >= 1280 && videoHeight >= 720) {
      quality = "HD";
    } else {
      quality = "SD";
    }
    resolve(quality);
  };
  video.onerror = () => {
    reject(new Error("Invalid video file"));
  };
  video.src = URL.createObjectURL(file);
});

var globalTheme = {
  color: {
    primary: "#477AE9",
    accordionTitle: "#4D5473",
    accordionTitleHover: "#2F3652",
    analyticsCardSortBackground: "#f3f7fe",
    approved: "#21BA45",
    avatarBackground: ["#CFDFFC", "#D0F0FD", "#C2F5E9", "#F9DAF6", "#FBE2D5", "#D1F7C4", "#EDE2FD", "#FFD4D4"],
    background: "#FCFCFC",
    backgroundError: "#FFE3E3",
    backgroundBanner: "#FFD1D1",
    backgroundMessage: "#FBE4E4",
    breadcrumbBackground: "#F0F4FB",
    button: "#477AE9",
    buttonActive: "#123C96",
    buttonHover: "#1954D3",
    buttonInverted: "#FFFFFF",
    buttonInvertedActive: "#BBBBBB",
    buttonInvertedHover: "#DDDDDD",
    buttonInvertedText: "#172B4D",
    buttonText: "#FFFFFF",
    cardSortBackground: "#F1F1F1",
    categorizingBackground: "#F1F1F2",
    categorizingBorder: "#CAD3DB",
    categorizingColor: "#535252",
    cellColorFirstStage: "#FFFFFF",
    cellColorSecondStage: "#EDF5FF",
    cellColorThirdStage: "#D2E6FF",
    cellColorFourthStage: "#A9CFFE",
    cellColorFifthStage: "#99C0F5",
    cellColorSixthStage: "#76A8EA",
    checkbox: "#4D5473",
    checkboxAccordion: "#ECECEC",
    comingSoon: "#71ffc7",
    dateColorText: "#BBBBBB",
    deleteLink: "#FC0303",
    deleteLinkActive: "#D90404",
    deleteLinkHover: "#F00000",
    directFailure: "#F93A52",
    directSuccess: "#33BF50",
    discount: "#93C47D",
    divider: "#DCD7D7",
    error: "#9F3A38",
    flag: "#BBE9FA",
    flagText: "#477AE9",
    highlight: "#DFE8FB",
    highlightWordColor: "#5E6C84",
    indirectFailure: "#FF8896",
    indirectSuccess: "#93FBA9",
    inputBackgroundError: "#FFF6F6",
    inputBorder: "#ECECEC",
    inputBorderError: "#E0B4B4",
    inputColor: "#4D5573",
    invertedDanger: "#EF2D2D",
    itemSelected: "#477AE9",
    link: "#477AE9",
    linkActive: "#123C96",
    linkColor: "#00B7F2",
    linkHover: "#1954D3",
    loader: "#477AE9",
    memberAccepted: "#8AEA69",
    memberPending: "#FFF9BA",
    memberRejected: "#EE596B",
    multipleDropdown: "#4D5473",
    multipleDropdownHover: "#2F3652",
    progress: "#5E6C84",
    progressOutline: "#E8E8E8",
    projectDone: "#2E7EF6",
    projectDraft: "#BBE9FA",
    projectInProgress: "#4BBFF8",
    questionTitleMultiple: "#EB715E",
    questionTitleRange: "#FFCD6E",
    questionTitleSingle: "#B289E7",
    questionTitleTask: "#6BC0E7",
    questionTitleUrl: "#5AC46A",
    questionTitleWritten: "#64DDD4",
    questionTitleImage: "#A1EB9B",
    questionTitlePreference: "#00A1DC",
    questionTitleFirstClickTest: "#9DA6FB",
    questionTitleVideo: "#EBBB9B",
    questionTitleMatrix: "#9BB3EB",
    questionTitleFiveSeconds: "#69CEAF",
    questionTitleRanking: "#FFC38F",
    removed: "#DB2828",
    rowHover: "#F7F7F7",
    savedTemplate: "#65BE39",
    searchInputBorder: "#DEDEDF",
    searchInputTextActive: "#4D5473",
    searchInputTextFocus: "#4D5473",
    searchInputTextHover: "#4D5473",
    segmentBorder: "#EFEFEF",
    segmentCreateProjectBackground: "#FEFEFE",
    selectedItemCompanyDashboard: "#172B4D",
    sidebar: "#FFFFFF",
    sidebarBorder: "#ECECEC",
    sidebarItems: "#5E6C84",
    sidebarSelectedItems: "#477AE9",
    sidebarTestType: "#5E6C84",
    sidebarTitle: "#5E6C84",
    success: "#21BA45",
    text: "#4D5473",
    textError: "#FF3F3F",
    textErrorProjetSetUp: "#ED3A4E",
    textFeedback: "#9095A8",
    textHeader: "#172B4D",
    textHeaderLarge: "#192c47",
    textHighlight: "#DCE6FB",
    textLabel: "#172B4D",
    textMuted: "#777777",
    textOptional: "#6E7678",
    textSecondary: "#192C47",
    textSignScreen: "#192C47",
    textTitle: "#172B4D",
    titleHighlightReels: "#192C47",
    toastBackgroundColor: "#DCE6FB",
    toastTextColor: "#4D5573",
    toggleBackground: "#f5f9fe",
    toggleTextColor: "#9bb9f5",
    tooltipBorder: "#F1F1F1",
    tooltipDescription: "#E5E5E5",
    warningHeader: "#FF3F3F",
    white: "#FFFFFF",
    writtenResponseBorder: "#EAEAEA",
    treeColor: "#FED7C1",
    cardColor: "#F5F0FE",
    surveyColor: "#FFE8E8"
  },
  fontSize: {
    button: "15px",
    checkboxText: "14px",
    text: "14px",
    textHeader: "16px",
    textMuted: "12px",
    textOptional: "10px",
    textTitle: "24px"
  },
  breakpoints: {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920
  }
};

var getCurrentTheme = () => {
  const platformType = process.env.REACT_APP_PLATFORM_TYPE;
  switch (platformType) {
    default:
      return globalTheme;
  }
};

function ThemeProvider({
  children
}) {
  return React.createElement(ThemeProvider$1, {
    theme: getCurrentTheme()
  }, children);
}

let _$2 = t => t,
  _t$2;
const GlobalStyles = createGlobalStyle(_t$2 || (_t$2 = _$2`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    outline: 0;
    direction: ${0};
  }

  html, body, #root {
    height: 100%;
    background-color: ${0};
    font-family: Roboto;
    color: ${0};
    -moz-osx-font-smoothing: grayscale;
    -webkit-font-smoothing: antialiased;
    font-smoothing: antialiased;
    font-weight: 400;
  }

  @-moz-document url-prefix() {
    body {
      font-weight: lighter !important;
    }
  }

  a {
    color: ${0};
    cursor: pointer;
    text-decoration: none;

    :hover {
      color: ${0};
    }

    :active {
      color: ${0};
    }
  }

  p {
    font-family: Roboto;
  }

  h1, h2, h3, h4, h5 {
    font-family: Roboto;
    font-weight: 500;
  }

  h1 {
    color: ${0};
    font-size: 20px;
  }

  h2 {
    color: ${0};
    font-size: 18px;
  }

  h3 {
    color: ${0};
    font-size: 16px;
  }

  h4 {
    color: ${0};
    font-size: 14px;
  }

  &.ui.header {
    font-family: Roboto;
    font-weight: 500;
  }

  &.ui.checkbox input.hidden + label {
    color: ${0};
  }

  &.ui.button {
    font-family: Roboto;
  }

  &.ui.menu .ui.dropdown .menu > .item {
    font-family: Roboto;
  }

  &.ui.dropdown .menu .active.item {
    font-weight: 500;
  }

  &.ui.form .ui.input input {
    border-radius: 2px;
    border: 1px solid ${0};
    font-family: Roboto;

    :focus {
      border-radius: 2px;
      border: 1px solid ${0};
    }
  }

  &.ui.input input {
    border-radius: 3px;
    font-family: Roboto;

    :focus {
      border-radius: 2px;
      border: 1px solid ${0};
    }
  }

  &.ui.form textarea {
    border-radius: 2px;
    border: 1px solid ${0};
    font-family: Roboto;

    :focus {
      border-radius: 2px;
      border: 1px solid ${0};
    }
  }

  &.ui.multiple.search.dropdown {
    border-radius: 2px;
    border: 1px solid ${0};
    box-shadow: none;
    font-family: Roboto;
  }

  &.ui.selection.active.dropdown .menu {
    border-radius: 2px;
    border: 1px solid ${0};
    box-shadow: none;
  }

  &.ui.search.selection.dropdown {
    border-radius: 2px;
    border: 1px solid ${0};
    box-shadow: none;
    font-family: Roboto;
  }

  &.ui.selection.active.dropdown:hover {
    border-radius: 2px;
    border: 1px solid ${0};
    box-shadow: none;
    font-family: Roboto;
  }

  &.ui.selection.active.dropdown:hover .menu {
    border-radius: 2px;
    border: 1px solid ${0};
    box-shadow: none;
  }

  &.ui.styled.accordion .title {
    color: ${0};
    font-family: Roboto;
    font-weight: 500;

    :hover {
      color: ${0};
    }
  }

  &.ui.dropdown .menu .selected.item {
    color: ${0};
  }

  &.ui.multiple.dropdown > .label {
    color: ${0};
    font-weight: 500;

    :hover {
      color: ${0};
    }
  }

  &.ui.dropdown .menu > .item {
    color: ${0};
  }

  &.ui.dropdown .menu > .item:hover {
    color: ${0};
  }

  &.ui.info.message {
    color: ${0};
    background: ${0};
    padding: 15px;

    &.ui.message {
      box-shadow: 0 0 black;
      margin: 10px 5px;
    }

    &.ui.icon.message > .icon:not(.close) {
      display: none;
    }

    &.ui.message > .close.icon {
      top: .7em;
      right: .3em;
    }
  }
`), props => props.direction === "rtl" ? "rtl" : "ltr", props => props.theme.color.background, ({
  theme
}) => theme.color.text, props => props.theme.color.link, props => props.theme.color.linkHover, props => props.theme.color.linkActive, props => props.theme.color.textHeaderLarge, props => props.theme.color.textHeaderLarge, props => props.theme.color.textHeader, props => props.theme.color.textHeader, props => props.theme.color.checkbox, props => props.theme.color.inputBorder, props => props.theme.color.primary, props => props.theme.color.primary, props => props.theme.color.inputBorder, props => props.theme.color.primary, props => props.theme.color.inputBorder, props => props.theme.color.inputBorder, props => props.theme.color.inputBorder, props => props.theme.color.primary, props => props.theme.color.primary, props => props.theme.color.accordionTitle, props => props.theme.color.accordionTitleHover, props => props.theme.color.text, props => props.theme.color.multipleDropdown, props => props.theme.color.multipleDropdownHover, ({
  theme
}) => theme.color.text, ({
  theme
}) => theme.color.text, props => props.theme.color.toastTextColor, props => props.theme.color.toastBackgroundColor);
var globalStyles = withTranslation(GlobalStyles);

const createStripeElementOptions = disabled => ({
  options: {
    disabled,
    style: {
      base: {
        color: "rgba(0,0,0,.95)",
        "::placeholder": {
          color: "rgba(191,191,191,.87)"
        }
      },
      invalid: {
        color: "#9F3A38"
      }
    }
  }
});

function StripeCardInput(props) {
  const {
    disabled = false,
    onSetCompleted,
    callback
  } = props;
  const stripe = useStripe();
  const elements = useElements();
  const handleStripeCallback = useCallback(() => {
    if (stripe && elements) {
      const cardElement = elements.getElement(CardElement);
      if (cardElement) {
        stripe.createToken(cardElement).then(payload => {
          if (payload.error) {
            Sentry.withScope(scope => {
              var _payload$error;
              scope.setExtra("object", payload);
              Sentry.captureException(payload == null || (_payload$error = payload.error) == null ? void 0 : _payload$error.message);
            });
            const _tokenId = null;
            callback(_tokenId);
            return;
          }
          const {
            token
          } = payload;
          const {
            id: tokenId
          } = token;
          callback(tokenId);
        });
      }
      callback(null);
    }
  }, [stripe, callback]);
  const handleChange = element => {
    const {
      complete
    } = element;
    onSetCompleted(complete);
    if (complete) {
      handleStripeCallback();
    }
  };
  return React.createElement(CardElement, _extends({
    onChange: handleChange
  }, createStripeElementOptions(disabled)));
}

let _$1 = t => t,
  _t$1,
  _t2$1,
  _t3$1;
const RadioButton$1 = styled(Radio)(_t$1 || (_t$1 = _$1``));
const Container = styled.div(_t2$1 || (_t2$1 = _$1`
  cursor: pointer;
  margin-bottom: 18px;
`));
const Label = styled.span(_t3$1 || (_t3$1 = _$1`
  margin-left: 5px;
`));

function PaymentMethod(props) {
  const {
    card,
    onChange,
    selectedCard,
    paymentMethodId,
    index
  } = props;
  const {
    brand,
    last4,
    expMonth,
    expYear
  } = card;
  const expiresAt = `${_$B.padStart(expMonth.toString(), 2, "0")}/${expYear}`;
  return React.createElement(Container, {
    onClick: () => onChange(paymentMethodId)
  }, React.createElement(RadioButton$1, {
    "data-payment-card": index,
    name: "single_select",
    value: paymentMethodId,
    checked: selectedCard === paymentMethodId
  }), React.createElement(Label, null, `${_$B.capitalize(brand)} ending in ${last4} expires ${expiresAt}`));
}

let _ = t => t,
  _t,
  _t2,
  _t3,
  _t4,
  _t5,
  _t6;
const Header = styled(Text$2.Header)(_t || (_t = _`
  font-weight: 400;
`));
const Checkbox = styled(Checkbox$2)(_t2 || (_t2 = _`
  margin-top: 10px;

  ${0}
`), props => props.addMore && `
    margin-left: 22px;
  `);
const Content = styled.div(_t3 || (_t3 = _`
  ${0}
  padding: 15px;
`), props => props.disabled && `
    pointer-events: none;
    opacity: 0.4;
  `);
const CardContainer = styled.div(_t4 || (_t4 = _`
  display: flex;
`));
const ButtonLink = styled(Button$5)(_t5 || (_t5 = _`
  &&& {
    background-color: transparent;
    color: #477ae9;
    text-decoration: none;
    font-weight: 400;
    padding: 0 0 0 4px;
  }
`));
const RadioButton = styled(Radio)(_t6 || (_t6 = _`
  margin-right: 5px;
`));

const HEADER = "Please enter with your payment information";
const LABEL = "Remember this card";
function StripePaymentMethods(props) {
  const {
    callback,
    disabled,
    onSetRememberCard,
    onSetSelectedCard,
    paymentMethods,
    rememberCard,
    selectedCard
  } = props;
  const [completed, setCompleted] = useState(false);
  const [previousCompleted, setPreviousCompleted] = useState(false);
  const [checked, setChecked] = useState(false);
  const handleClick = () => {
    setChecked(true);
    onSetSelectedCard(null);
  };
  useEffect(() => {
    if (completed !== previousCompleted) {
      setPreviousCompleted(completed);
      if (!completed) {
        callback(null);
      }
    }
  }, [callback, completed, previousCompleted]);
  const showInput = checked && !selectedCard;
  const hasSaveCards = !_$B.isEmpty(paymentMethods);
  return React.createElement(React.Fragment, null, React.createElement(Header, null, HEADER), React.createElement(Content, {
    disabled: disabled
  }, hasSaveCards && React.createElement(React.Fragment, null, paymentMethods.map(({
    id,
    card
  }, index) => React.createElement(PaymentMethod, {
    index: index,
    key: id,
    card: card,
    onChange: onSetSelectedCard,
    paymentMethodId: id,
    selectedCard: selectedCard
  })), !showInput && React.createElement(ButtonLink, {
    onClick: handleClick,
    "data-button-add-new-card": true
  }, "+ Add a new card")), (!hasSaveCards || showInput) && React.createElement(React.Fragment, null, React.createElement(CardContainer, null, showInput && React.createElement(RadioButton, {
    name: "single_select",
    checked: checked,
    "data-single-select": true
  }), React.createElement(StripeCardInput, {
    disabled: false,
    onSetCompleted: setCompleted,
    callback: callback
  })), React.createElement(Checkbox, {
    "data-remember-card": true,
    addMore: showInput,
    name: "remember_card",
    label: LABEL,
    checked: rememberCard,
    disabled: !completed,
    onChange: () => onSetRememberCard(!rememberCard)
  }))));
}
StripePaymentMethods.propTypes = {
  callback: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  onSetRememberCard: PropTypes.func.isRequired,
  onSetSelectedCard: PropTypes.func.isRequired,
  paymentMethods: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    card: PropTypes.objectOf(PropTypes.string)
  })).isRequired,
  rememberCard: PropTypes.bool,
  selectedCard: PropTypes.string
};
StripePaymentMethods.defaultProps = {
  disabled: false,
  selectedCard: null,
  rememberCard: false
};

export { ALL_SCOPES, AUTHENTICATION_SCOPES$1 as AUTHENTICATION_SCOPES, Button$4 as Button, CenteredLoader, Checkbox$1 as Checkbox, ConfirmDialog, ContainerToggle, CopyInput, DownloadModal, EDITOR_STATUS, EntityTypes$1 as EntityTypes, ErrorHandler, globalStyles as GlobalStyles, Header$1 as Header, IfScope, IfScopeProvider, InfiniteScrollComponent, index as InputFormField, InputNumberField, Loader$4 as Loader, Media, MediaContext, Notes, PolymorphicContainer, RichTextEditor, ScrollBarContainer, Segment$2 as Segment, ShareClipDialog, ShareLinkDialog$1 as ShareLinkDialog, StripeCardInput, StripePaymentMethods, Tag, TagListFromTagHighlights, Text$2 as Text, index$1 as TextAreaFormField, TextHighlighted, TextHighlighter, TextToggle, ThemeProvider, Toggle, TranslationProvider, UpgradeButton, WebScopeProvider, axiosThirdParty, captureException, captureGQLException, colors, convertFromRichText, convertToPlainText, convertToRichText, createEditorStateFromNote, createEmptyState, createFromText, createTextSegments, extractMentionHighlights$1 as extractMentionHighlights, formatMiliseconds, formatSeconds, formatSecondsObject, globalTheme, mapTime, offsetLimitPagination, pushStateChange, reactionsMap, sendInfo, useCompanyMembers, useCreateBillingPortalMutation, useErrorHandling, useFeatureFlags, useFileValidation, useIfScope, useMedia, usePaginatedQuery, usePresignedUrl, useRequestUpload, useWebScope, validateVideoQuality, withMedia, withTranslation };
